import React, {
  useMemo,
  useReducer,
  useState,
  useEffect,
  useCallback,
  useRef
} from "react";
import DOMPurify from "dompurify";
import { useParams, useNavigate } from "react-router-dom";
import { showToast } from "../../ui/toast";
import {
  FiEdit2,
  FiUserPlus,
  FiPlusCircle,
  FiTrash,
  FiMoreHorizontal,
  FiShare,
  FiLogIn,
  FiAlertTriangle,
  FiCheck,
  FiClock,
  FiRotateCcw,
  FiX,
  FiSearch,
  FiPaperclip,
  FiLink,
  FiChevronLeft,
  FiChevronDown,
  FiActivity,
  FiDownload,
  FiSave,
  FiCheckSquare,
  FiSliders,
  FiTag,
  FiUsers,
  FiMessageSquare
} from "react-icons/fi";
import Header from "../../components/Header/Header";
import ProjectCardLoader from "../../components/ProjectCardLoader";
import { uploadFile } from "../../services/firebaseStorageService";
import Cookies from "js-cookie";
import useBugs from "../../hooks/useBugs";
import BugModal from "./modals/BugModal";
import VersionModal from "./modals/VersionModal";
import UserModal from "./modals/UserModal";
import InfoModal from "./modals/InfoModal";
import DeleteModal from "./modals/DeleteModal";
import ShareModal from "./modals/ShareModal";
import RenameVersionModal from "./modals/RenameVersionModal";
import DeadlineModal from "./modals/DeadlineModal";
import "./Bugs.css";
import { shareProjectInvite } from "../../services/ShareService";
import { updateBug, getBugsByProject, getBugDetails } from "../../services/BugService";
import {
  buildAddedBugAiPrompt,
  buildBugDevelopmentPrompt,
  buildBugDevelopmentPromptFromForm,
  buildBugTestScenarioPrompt,
  buildBugTestScenarioPromptFromForm,
  normalizeAddedBugForPrompt
} from "../../services/BugPromptService";
import EditProjectModal from "./modals/EditProjectModal";
import Modal from "../../components/Modal/Modal";
import BugDetail from "../BugDetail/BugDetail";
import { createInvite } from "../../services/InviteService";
import { getAllProjects } from "../../services/ProjectService";
import {
  getLabels,
  attachLabelToBug,
  detachLabelFromBug,
  getBugLabels
} from "../../services/LabelService";
import {
  saveBugMeta,
  PRIORITY_LABELS,
  PRIORITY_COLORS
} from "../../services/BugMetaService";
import {
  getChecklist,
  addChecklistItem,
  updateChecklistItem,
  removeChecklistItem
} from "../../services/BugChecklistService";
import {
  getAssignees,
  addAssignee,
  removeAssignee
} from "../../services/BugAssigneeService";
import { getCommentsByBug, createComment } from "../../services/CommentService";
import FilePreviewModal, { detectFilePreviewKind } from "../../components/FilePreviewModal";

const VERSION_META_PREFIX = "buglog:versionMeta:";
const GLOBAL_VERSION_INDEX_KEY = "buglog:globalVersionIndex";
const SAVED_FILTERS_PREFIX = "buglog:savedFilters:";
const MAX_SAVED_FILTERS = 12;

const VERSION_UID_PREFIX = "BL-";
const VERSION_ID_MIN_LEN = 6;

const safeParse = (v) => {
  try {
    if (!v) return null;
    return JSON.parse(v);
  } catch {
    return null;
  }
};

const safeStringify = (v) => {
  try {
    return JSON.stringify(v);
  } catch {
    return "";
  }
};

const readSavedBugFilters = (projectId) => {
  try {
    const key = `${SAVED_FILTERS_PREFIX}${String(projectId || "global")}`;
    const parsed = safeParse(localStorage.getItem(key));
    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((item) => ({
        id: String(item?.id || "").trim() || `filter-${Date.now()}`,
        name: String(item?.name || "").trim(),
        search: String(item?.search || ""),
        statusFilter: ["all", "open", "completed"].includes(item?.statusFilter)
          ? item.statusFilter
          : "all",
        versionFilter: String(item?.versionFilter || "all"),
        dateFilter: ["all", "today", "last7", "last30"].includes(item?.dateFilter)
          ? item.dateFilter
          : "all",
        labelFilter: String(item?.labelFilter || "all"),
        createdAt: Number(item?.createdAt || Date.now())
      }))
      .filter((item) => item.name);
  } catch {
    return [];
  }
};

const writeSavedBugFilters = (projectId, filters) => {
  try {
    const key = `${SAVED_FILTERS_PREFIX}${String(projectId || "global")}`;
    localStorage.setItem(key, safeStringify((filters || []).slice(0, MAX_SAVED_FILTERS)));
  } catch {
  }
};

const toBool = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  value === "true" ||
  value === "yes";

const normalizeId = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const getProjectDisplayName = (project = {}) => {
  try {
    const key = project?.uniq || project?.id;
    const stored = key ? localStorage.getItem(`title:${key}`) || "" : "";
    if (stored && String(stored).trim()) return String(stored).trim();
  } catch {
  }

  return String(project?.name || project?.project_name || project?.title || "").trim();
};

const safeDecodeParam = (value = "") => {
  try {
    return decodeURIComponent(String(value || ""));
  } catch {
    return String(value || "");
  }
};

const readStoredProjectTitle = (projectId) => {
  try {
    const stored = projectId ? localStorage.getItem(`title:${projectId}`) || "" : "";
    return String(stored || "").trim();
  } catch {
    return "";
  }
};

const canManageProjectAccess = (project, currentUserId) => {
  const creatorId = normalizeId(
    project?.created_by ??
      project?.created_by_id ??
      project?.owner_id ??
      project?.user_id
  );

  const rawRole = String(
    project?.role ??
      project?.member_role ??
      project?.project_role ??
      project?.permission ??
      ""
  )
    .trim()
    .toLowerCase();

  if (
    toBool(project?.is_owner) ||
    toBool(project?.owner) ||
    toBool(project?.is_admin) ||
    toBool(project?.admin) ||
    toBool(project?.is_manager) ||
    toBool(project?.manager)
  ) {
    return true;
  }

  if (["owner", "admin", "administrator", "manager", "yönetici", "yonetici"].includes(rawRole)) {
    return true;
  }

  return !!(currentUserId && creatorId && currentUserId === creatorId);
};

const getUidDigits = (value) => {
  const t = String(value || "").trim().toUpperCase();
  if (!t) return "";
  if (t.startsWith(VERSION_UID_PREFIX)) return t.slice(VERSION_UID_PREFIX.length).replace(/\D/g, "");
  return t.replace(/\D/g, "");
};

const normalizeVersionUid = (value) => {
  const digits = getUidDigits(value);
  if (!digits) return "";
  return `${VERSION_UID_PREFIX}${digits}`;
};

const isVersionUid = (value) => {
  const uid = normalizeVersionUid(value);
  if (!uid) return false;
  const m = uid.match(/^BL-(\d+)$/);
  if (!m) return false;
  return m[1].length >= VERSION_ID_MIN_LEN;
};

const isLegacyPrefixedId = (value) => {
  const s = String(value || "").trim().toUpperCase();
  return /^V-\d+$/.test(s) || /^V\d+$/.test(s);
};

const readGlobalVersionIndex = () => {
  try {
    const raw = localStorage.getItem(GLOBAL_VERSION_INDEX_KEY);
    const parsed = safeParse(raw);

    const base =
      parsed && typeof parsed === "object"
        ? parsed
        : { byVersionKey: {}, byId: {} };

    const byVersionKey =
      base.byVersionKey && typeof base.byVersionKey === "object" ? base.byVersionKey : {};
    const byId = base.byId && typeof base.byId === "object" ? base.byId : {};

    return { byVersionKey, byId };
  } catch {
    return { byVersionKey: {}, byId: {} };
  }
};

const writeGlobalVersionIndex = (idx) => {
  try {
    localStorage.setItem(GLOBAL_VERSION_INDEX_KEY, safeStringify(idx || {}));
  } catch {
  }
};

const genVersionUid = (byId) => {
  const used = byId && typeof byId === "object" ? byId : {};
  for (let i = 0; i < 40; i++) {
    let n = 0;
    try {
      if (typeof crypto !== "undefined" && crypto.getRandomValues) {
        const arr = new Uint32Array(2);
        crypto.getRandomValues(arr);
        const a = arr[0] >>> 0;
        const b = arr[1] >>> 0;
        n = (a * 1000000000 + (b % 1000000000)) % 9000000000;
      } else {
        n = Math.floor(Math.random() * 9000000000);
      }
    } catch {
      n = Math.floor(Math.random() * 9000000000);
    }

    const digits = String(1000000000 + (n % 9000000000));
    const uid = `${VERSION_UID_PREFIX}${digits}`;
    if (!used[uid]) return uid;
  }

  const fallbackDigits = String(Date.now());
  const fallbackUid = `${VERSION_UID_PREFIX}${fallbackDigits}`;
  if (!used?.[fallbackUid]) return fallbackUid;
  return `${fallbackUid}${Math.floor(Math.random() * 1000)}`;
};

const buildGlobalVersionKey = (projectUniq, version) => {
  const p = String(projectUniq || "").trim();
  const vid = version?.id != null ? String(version.id).trim() : "";
  const vname = version?.version_name != null ? String(version.version_name).trim() : "";
  if (!p) return "";
  if (vid) return `p:${p}|vid:${vid}`;
  if (vname) return `p:${p}|vname:${vname}`;
  return "";
};

const readVersionMetaMap = (projectId) => {
  try {
    if (!projectId) return {};
    const raw = localStorage.getItem(`${VERSION_META_PREFIX}${projectId}`);
    const parsed = safeParse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return parsed;
  } catch {
    return {};
  }
};

const writeVersionMetaMap = (projectId, map) => {
  try {
    if (!projectId) return;
    localStorage.setItem(`${VERSION_META_PREFIX}${projectId}`, safeStringify(map || {}));
  } catch {
  }
};

const resolveMetaKey = (version) => {
  const id = version?.id != null ? String(version.id) : "";
  const name = version?.version_name ? String(version.version_name) : "";
  if (id) return `id:${id}`;
  if (name) return `name:${name}`;
  return "";
};

const getVersionMeta = (projectId, version) => {
  const map = readVersionMetaMap(projectId);
  const k = resolveMetaKey(version);
  if (!k) return null;
  const v = map[k];
  if (!v || typeof v !== "object") return null;
  return v;
};

const patchVersionMeta = (projectId, version, patch) => {
  const map = readVersionMetaMap(projectId);
  const k = resolveMetaKey(version);
  if (!k) return;

  const prev = map[k] && typeof map[k] === "object" ? map[k] : {};
  const next = { ...prev, ...(patch || {}) };

  if (typeof next.deadline === "undefined") next.deadline = prev.deadline || "";
  if (typeof next.closed_at === "undefined") next.closed_at = prev.closed_at || "";

  map[k] = next;
  writeVersionMetaMap(projectId, map);

  try {
    window.dispatchEvent(new CustomEvent("version:changed", { detail: { projectId } }));
  } catch {
  }
};

const applyMetaToVersion = (projectId, version) => {
  if (!version) return version;
  const meta = getVersionMeta(projectId, version);
  if (!meta) return version;

  const out = { ...version };

  if (typeof meta.deadline === "string") out.deadline = meta.deadline;
  if (typeof meta.closed_at === "string") out.closed_at = meta.closed_at;

  return out;
};

const decodeHtmlEntities = (value = "") => {
  const raw = String(value || "");
  if (!raw) return "";

  try {
    if (typeof document !== "undefined") {
      const textarea = document.createElement("textarea");
      textarea.innerHTML = raw;
      return textarea.value || textarea.textContent || "";
    }
  } catch {
  }

  return raw
    .replace(/&nbsp;/gi, " ")
    .replace(/&#160;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, "\"")
    .replace(/&#39;/gi, "'");
};

const htmlToText = (s = "") => {
  if (!s) return "";
  try {
    let str = String(s);
    str = str.replace(/<li[^>]*>/gi, "• ");
    str = str.replace(/<br\s*\/?>/gi, "\n");
    str = str.replace(/<\/(p|div|li|ul|ol|h[1-6])>/gi, "\n");
    str = str.replace(/<[^>]*>/g, "");
    str = decodeHtmlEntities(str);
    str = str.replace(/\u00a0/g, " ");
    str = str
      .split("\n")
      .map((line) => line.replace(/\s+/g, " ").trim())
      .filter((line, i, a) => !(line === "" && a[i - 1] === ""))
      .join("\n")
      .trim();
    return str;
  } catch {
    return String(s)
      .replace(/<li[^>]*>/gi, "• ")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li|ul|ol|h[1-6])>/gi, "\n")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, " ")
      .replace(/&#160;/gi, " ")
      .replace(/\u00a0/g, " ")
      .split("\n")
      .map((line) => line.replace(/\s+/g, " ").trim())
      .filter((line, i, a) => !(line === "" && a[i - 1] === ""))
      .join("\n")
      .trim();
  }
};

const getFileName = (u = "") => {
  try {
    const url = new URL(u);
    return decodeURIComponent(url.pathname.split("/").pop() || "dosya");
  } catch {
    const clean = (u || "").split("?")[0];
    return decodeURIComponent(clean.split("/").pop() || "dosya");
  }
};

const getHost = (u = "") => {
  try {
    const url = new URL(u);
    return url.host;
  } catch {
    return (u || "").replace(/^https?:\/\//, "").split("/")[0];
  }
};

const isFirebaseStorageUrl = (u = "") => {
  try {
    const url = new URL(u);
    return url.host === "firebasestorage.googleapis.com";
  } catch {
    return false;
  }
};

const getFirebaseObjectPath = (u = "") => {
  try {
    const url = new URL(u);
    if (url.host !== "firebasestorage.googleapis.com") return "";
    const p = url.pathname || "";
    const idx = p.indexOf("/o/");
    if (idx === -1) return "";
    const encoded = p.slice(idx + 3);
    if (!encoded) return "";
    const decoded = decodeURIComponent(encoded);
    return decoded || "";
  } catch {
    return "";
  }
};

const getDisplaySource = (u = "") => {
  const objPath = getFirebaseObjectPath(u);
  if (objPath) return objPath;
  return getHost(u);
};

const getCover = (uniq) => {
  try {
    return uniq ? localStorage.getItem(`cover:${uniq}`) || "" : "";
  } catch {
    return "";
  }
};

const getColor = (uniq) => {
  try {
    return uniq ? localStorage.getItem(`color:${uniq}`) || "" : "";
  } catch {
    return "";
  }
};

const isImageUrl = (u = "") => detectFilePreviewKind(u) === "image";

const modalInit = {
  bug: false,
  version: false,
  user: false,
  info: false,
  deleteConfirm: false,
  share: false
};
const reducer = (s, a) => ({ ...s, [a.type]: a.open });

const initialForm = {
  title: "",
  description: "",
  file: null,
  fileUrl: "",
  uploadStatus: "",
  version: "",
  email: "",
  confirmName: "",
  preview: "",
  nowVersion: "",
  externalUrl: "",
  labels: [],
  priority: "medium",
  dueDate: ""
};

const formatDueDateShort = (val) => {
  if (!val) return "";
  const raw = String(val).trim();
  const isoLike = raw.includes(" ") && !raw.includes("T") ? raw.replace(" ", "T") : raw;
  const d = new Date(isoLike);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const isBugOverdue = (bug) => {
  const due = bug?.meta?.due_date;
  if (!due) return false;
  const done = toBool(bug?.is_completed) || toBool(bug?.completed) || bug?.status === "on";
  if (done) return false;
  const raw = String(due).trim();
  const isoLike = raw.includes(" ") && !raw.includes("T") ? raw.replace(" ", "T") : raw;
  const d = new Date(isoLike);
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
};

const readMyUserId = () => {
  try {
    const uid =
      localStorage.getItem("user_id") ||
      localStorage.getItem("userId") ||
      localStorage.getItem("uid") ||
      Cookies.get("user_id") ||
      Cookies.get("uid") ||
      null;
    return uid != null ? String(uid) : "";
  } catch {
    return "";
  }
};

const normalizeMemberEmail = (value) => String(value || "").trim().toLowerCase();

const isCurrentProjectUser = (user, currentUserId, currentUserEmail) => {
  const memberId = String(user?.id ?? user?.user_id ?? "").trim();
  const memberEmail = normalizeMemberEmail(user?.email);

  if (currentUserId && memberId && memberId === String(currentUserId).trim()) {
    return true;
  }

  if (currentUserEmail && memberEmail && memberEmail === normalizeMemberEmail(currentUserEmail)) {
    return true;
  }

  return false;
};

const getProjectUserRoleLabel = (role) => {
  if (role === "owner") return "Sahip";
  if (role === "manager") return "Yönetici";
  return "Üye";
};

const getBugTimeMs = (bug = {}) => {
  const raw =
    bug.updated_at ||
    bug.created_at ||
    bug.createdAt ||
    bug.created ||
    bug.date ||
    "";
  if (!raw) return 0;
  const value = String(raw).trim();
  const isoLike = value.includes(" ") && !value.includes("T") ? value.replace(" ", "T") : value;
  const parsed = new Date(isoLike);
  if (!Number.isNaN(parsed.getTime())) return parsed.getTime();
  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? 0 : fallback.getTime();
};

const startOfTodayMs = () => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now.getTime();
};

const matchesDateFilter = (bug, filter) => {
  if (!filter || filter === "all") return true;
  const ts = getBugTimeMs(bug);
  if (!ts) return false;
  const now = Date.now();

  if (filter === "today") return ts >= startOfTodayMs();
  if (filter === "last7") return ts >= now - 7 * 24 * 60 * 60 * 1000;
  if (filter === "last30") return ts >= now - 30 * 24 * 60 * 60 * 1000;

  return true;
};

const getBugSearchText = (bug = {}) =>
  [
    bug.title,
    bug.bug_title,
    htmlToText(bug.description || ""),
    bug.email,
    bug.version_name,
    bug.file_url,
    bug.external_url
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const getBugKey = (bug = {}) => String(bug?.id ?? bug?.bug_id ?? "").trim();

const COMPLETION_GRACE_MS = 5000;

const isBugDone = (bug = {}) =>
  toBool(bug?.is_completed) || toBool(bug?.completed) || bug?.status === "on";

const csvCell = (value = "") => {
  const text = String(value ?? "").replace(/\r?\n/g, " ").replace(/\s+/g, " ").trim();
  return `"${text.replace(/"/g, '""')}"`;
};

const csvDate = (value = "") => {
  if (!value) return "";
  const raw = String(value).trim();
  const isoLike = raw.includes(" ") && !raw.includes("T") ? raw.replace(" ", "T") : raw;
  const parsed = new Date(isoLike);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const csvFileName = (name = "proje") => {
  const clean = String(name || "proje")
    .toLowerCase()
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ı/g, "i")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${clean || "proje"}-isler.csv`;
};

const withBugParam = (path, bugId) => {
  try {
    const base = String(path || "").trim() || "/";
    const cleanBase = base.split("#")[0];
    const parts = cleanBase.split("?");
    const p = parts[0] || "/";
    const qs = new URLSearchParams(parts[1] || "");
    if (bugId != null && String(bugId).trim()) {
      qs.set("bug", String(bugId).trim());
    }
    const q = qs.toString();
    return q ? `${p}?${q}` : p;
  } catch {
    return String(path || "/");
  }
};

const notifyProjectUsers = async (payload) => {
  try {
    const res = await fetch("/notifications.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload || {})
    });
    return await res.json();
  } catch {
    return null;
  }
};

const Bugs = () => {
  const { projectId, isOwner, projectName } = useParams();
  const navigate = useNavigate();
  const [storedProjectTitle, setStoredProjectTitle] = useState(() => readStoredProjectTitle(projectId));
  const routeProjectName = useMemo(() => safeDecodeParam(projectName), [projectName]);
  const originalProjectNameRef = useRef(routeProjectName || "Proje");
  const previousProjectIdRef = useRef(projectId);
  const displayProjectName = useMemo(
    () => storedProjectTitle || routeProjectName || "Proje",
    [routeProjectName, storedProjectTitle]
  );
  const {
    bugs,
    versions,
    users,
    loading,
    createBug,
    toggleBug,
    removeBug,
    createVersion,
    renameVersion,
    addUser,
    removeUser,
    changeUserRole,
    leave,
    removeProject,
    currentUserId,
    currentUserEmail,
    canManageMembers
  } = useBugs(projectId, displayProjectName);

  useEffect(() => {
    if (previousProjectIdRef.current !== projectId) {
      previousProjectIdRef.current = projectId;
      originalProjectNameRef.current = routeProjectName || "Proje";
      setStoredProjectTitle(readStoredProjectTitle(projectId));
    }
  }, [projectId, routeProjectName]);

  const [modal, dispatch] = useReducer(reducer, modalInit);
  const [form, setForm] = useState(initialForm);
  const [isEdit, setIsEdit] = useState(false);
  const [editingBugId, setEditingBugId] = useState(null);
  const [shadowBugs, setShadowBugs] = useState(null);

  const [renameDlg, setRenameDlg] = useState({
    open: false,
    id: null,
    name: ""
  });
  const [deadlineDlg, setDeadlineDlg] = useState({
    open: false,
    id: null,
    date: ""
  });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [versionFilter, setVersionFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [labelFilter, setLabelFilter] = useState("all");
  const [projectLabels, setProjectLabels] = useState([]);
  const [savedFilters, setSavedFilters] = useState(() => readSavedBugFilters(projectId));
  const [savedFilterName, setSavedFilterName] = useState("");
  const [activeSavedFilterId, setActiveSavedFilterId] = useState("");
  const [selectedBugIds, setSelectedBugIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [completionGrace, setCompletionGrace] = useState({});
  const completionTimersRef = useRef({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [quickPanel, setQuickPanel] = useState({ bugId: "", tab: "" });
  const [quickData, setQuickData] = useState({});
  const [quickBusy, setQuickBusy] = useState(false);
  const [quickDraft, setQuickDraft] = useState({ checklist: "", comment: "", label: "", assignee: "" });
  const [bulkDeleteDlg, setBulkDeleteDlg] = useState(false);
  const token = Cookies.get("token");
  const canManageProjectUsers = canManageMembers || isOwner === "1";

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);

  const [detailId, setDetailId] = useState(null);

  const [deleteBugDlg, setDeleteBugDlg] = useState({
    open: false,
    id: null,
    title: ""
  });

  const [attViewer, setAttViewer] = useState({
    open: false,
    url: "",
    title: "",
    kind: ""
  });
  const [projectPanelOpen, setProjectPanelOpen] = useState(false);
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectOptionsLoading, setProjectOptionsLoading] = useState(false);
  const [projectSort, setProjectSort] = useState("az");

  const initials = useMemo(
    () =>
      displayProjectName
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase(),
    [displayProjectName]
  );

  useEffect(() => {
    let alive = true;
    setProjectOptionsLoading(true);

    getAllProjects()
      .then((list) => {
        if (!alive) return;
        setProjectOptions(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!alive) return;
        setProjectOptions([]);
      })
      .finally(() => {
        if (alive) {
          setProjectOptionsLoading(false);
        }
      });

    return () => {
      alive = false;
    };
  }, [projectId]);

  useEffect(() => {
    setSavedFilters(readSavedBugFilters(projectId));
    setSavedFilterName("");
    setActiveSavedFilterId("");
  }, [projectId]);

  const sortedProjectOptions = useMemo(() => {
    const list = [...projectOptions].filter((project) => String(project?.uniq || project?.id || "").trim());

    list.sort((a, b) => {
      const an = getProjectDisplayName(a);
      const bn = getProjectDisplayName(b);
      const result = an.localeCompare(bn, "tr", { sensitivity: "base" });
      return projectSort === "za" ? result * -1 : result;
    });

    return list;
  }, [projectOptions, projectSort]);

  const handleProjectSwitch = useCallback(
    (project) => {
      const targetProjectId = String(project?.uniq || project?.id || "").trim();
      if (!targetProjectId) return;

      const nextProjectName = getProjectDisplayName(project) || "Proje";
      const nextCanManage = canManageProjectAccess(project, currentUserId) ? 1 : 0;

      dispatch({ type: "info", open: false });
      navigate(`/bugs/${targetProjectId}/${nextCanManage}/${encodeURIComponent(nextProjectName)}`);
    },
    [navigate, currentUserId]
  );

  const handleSaveProjectTitle = useCallback(
    async (targetProjectId, nextTitle) => {
      const targetId = String(targetProjectId || projectId || "").trim();
      const title = String(nextTitle || "").trim();
      if (!targetId || !title) {
        throw new Error("Proje başlığı boş olamaz.");
      }

      try {
        localStorage.setItem(`title:${targetId}`, title);
      } catch {
      }

      setStoredProjectTitle(title);
      setProjectOptions((prev) =>
        prev.map((project) => {
          const key = String(project?.uniq || project?.id || "").trim();
          if (key !== targetId) return project;
          return {
            ...project,
            name: title,
            project_name: title,
            title
          };
        })
      );

      try {
        window.dispatchEvent(
          new CustomEvent("project:changed", {
            detail: { projectId: targetId, title }
          })
        );
      } catch {
      }

      if (targetId === String(projectId || "").trim()) {
        navigate(`/bugs/${projectId}/${isOwner}/${encodeURIComponent(title)}`, { replace: true });
      }
    },
    [isOwner, navigate, projectId]
  );

  const handleResetProjectTitle = useCallback(
    async (targetProjectId) => {
      const targetId = String(targetProjectId || projectId || "").trim();
      if (!targetId) return;

      try {
        localStorage.removeItem(`title:${targetId}`);
      } catch {
      }

      const fallbackTitle = originalProjectNameRef.current || "Proje";
      setStoredProjectTitle("");
      setProjectOptions((prev) =>
        prev.map((project) => {
          const key = String(project?.uniq || project?.id || "").trim();
          if (key !== targetId) return project;
          return {
            ...project,
            name: fallbackTitle,
            project_name: fallbackTitle,
            title: fallbackTitle
          };
        })
      );

      try {
        window.dispatchEvent(
          new CustomEvent("project:changed", {
            detail: { projectId: targetId, title: fallbackTitle }
          })
        );
      } catch {
      }

      if (targetId === String(projectId || "").trim()) {
        navigate(`/bugs/${projectId}/${isOwner}/${encodeURIComponent(fallbackTitle)}`, { replace: true });
      }
    },
    [isOwner, navigate, projectId]
  );

  const [coverTick, setCoverTick] = useState(0);
  const [versionTick, setVersionTick] = useState(0);
  const [versionUidTick, setVersionUidTick] = useState(0);

  useEffect(() => {
    const onChanged = (e) => {
      if (!e?.detail?.projectId || e.detail.projectId === projectId) {
        setCoverTick((x) => x + 1);
        setVersionTick((x) => x + 1);
        setEditModalOpen(false);
      }
    };

    const onProjectChanged = (e) => {
      if (!e?.detail?.projectId || e.detail.projectId === projectId) {
        setStoredProjectTitle(readStoredProjectTitle(projectId));
        setEditModalOpen(false);
      }
    };

    const onStorage = (e) => {
      const coverKey = `cover:${projectId}`;
      const colorKey = `color:${projectId}`;
      const titleKey = `title:${projectId}`;
      const versionKey = `${VERSION_META_PREFIX}${projectId}`;

      if (e.key === coverKey || e.key === colorKey) {
        setCoverTick((x) => x + 1);
        setEditModalOpen(false);
      }

      if (e.key === titleKey) {
        setStoredProjectTitle(readStoredProjectTitle(projectId));
        setEditModalOpen(false);
      }

      if (e.key === versionKey) {
        setVersionTick((x) => x + 1);
      }

      if (e.key === GLOBAL_VERSION_INDEX_KEY) {
        setVersionUidTick((x) => x + 1);
      }
    };

    window.addEventListener("cover:changed", onChanged);
    window.addEventListener("project:changed", onProjectChanged);
    window.addEventListener("version:changed", onChanged);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("cover:changed", onChanged);
      window.removeEventListener("project:changed", onProjectChanged);
      window.removeEventListener("version:changed", onChanged);
      window.removeEventListener("storage", onStorage);
    };
  }, [projectId]);

  const cover = useMemo(() => getCover(projectId), [projectId, coverTick]);
  const color = useMemo(() => getColor(projectId), [projectId, coverTick]);

  const refreshProjectLabels = useCallback(() => {
    if (!projectId) {
      setProjectLabels([]);
      return Promise.resolve([]);
    }
    return getLabels(projectId)
      .then((list) => {
        const next = Array.isArray(list) ? list : [];
        setProjectLabels(next);
        return next;
      })
      .catch(() => {
        setProjectLabels([]);
        return [];
      });
  }, [projectId]);

  useEffect(() => {
    refreshProjectLabels();
  }, [refreshProjectLabels]);

  useEffect(() => {
    if (modal.bug) refreshProjectLabels();
  }, [modal.bug, refreshProjectLabels]);

  useEffect(() => {
    const root = document.documentElement;
    const fallback = "#0169ff";
    const valid = typeof color === "string" && color.trim().length > 0 ? color.trim() : null;
    root.style.setProperty("--accent", valid || fallback);
    return () => {
      root.style.setProperty("--accent", fallback);
    };
  }, [color]);

  useEffect(() => {
    setShadowBugs(null);
    setSelectedBugIds([]);
  }, [projectId]);

  const dataBugs = shadowBugs || bugs;

  const lastVersionRaw = versions[versions.length - 1] || null;

  const lastVersion = useMemo(
    () => applyMetaToVersion(projectId, lastVersionRaw),
    [projectId, lastVersionRaw, versionTick]
  );

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  const countdownInfo = useMemo(() => {
    if (!lastVersion?.deadline) return null;
    const ms = new Date(lastVersion.deadline) - now;
    if (ms <= 0) return { text: "Süre doldu!", pct: 100, critical: true };

    const created = lastVersion?.created_at ? new Date(lastVersion.created_at) : null;
    const deadline = new Date(lastVersion.deadline);

    const total = created ? deadline - created : 0;
    const pct = total > 0 ? Math.min(100, ((total - ms) / total) * 100) : 0;

    let level = "ok";
    if (pct > 66) level = "warn";
    if (ms < 86400000) level = "danger";
    const d = Math.floor(ms / 86400000);
    const h = Math.floor((ms % 86400000) / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return { text: `${d}g ${h}s ${m}d`, pct, level };
  }, [lastVersion, now]);

  const handleFileUpload = async () => {
    if (!form.file) return "";
    setForm((f) => ({ ...f, uploadStatus: "Yükleniyor..." }));
    try {
      const url = await uploadFile(form.file);
      setForm((f) => ({
        ...f,
        fileUrl: url,
        uploadStatus: "Yükleme başarılı!"
      }));
      return url;
    } catch {
      setForm((f) => ({ ...f, uploadStatus: "Yükleme başarısız!" }));
      return "";
    }
  };

  const ensureFileUrl = useCallback(async () => {
    const already = (form.fileUrl || "").trim();
    if (already) return already;
    if (!form.file) return "";
    const url = await handleFileUpload();
    return (url || "").trim();
  }, [form.file, form.fileUrl]);

  const groupByVersion = useCallback((list) => {
    const map = {};
    (list || []).forEach((b) => {
      const key = (b.version_name || "v1.0").toString();
      if (!map[key]) map[key] = [];
      map[key].push(b);
    });
    return map;
  }, []);

  const refreshShadow = useCallback(async () => {
    const freshList = await getBugsByProject(projectId);
    setShadowBugs(groupByVersion(freshList));
  }, [projectId, groupByVersion]);

  const copyText = useCallback(async (text, feedback = {}) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      showToast({
        title: feedback.successTitle || "Kopyalandı",
        description: feedback.successDescription || "Panoya kopyalandı.",
        variant: "success",
        position: "bottom-right"
      });
    } catch {
      showToast({
        title: feedback.errorTitle || "Kopyalama başarısız",
        description: feedback.errorDescription || "Elle kopyalamanız gerekebilir.",
        variant: "error",
        position: "bottom-right"
      });
    }
  }, []);

  const getCurrentBugUrl = useCallback((bugId) => {
    try {
      if (typeof window === "undefined") return "";
      const path = withBugParam(window.location.pathname || "/", bugId);
      return `${window.location.origin}${path}`;
    } catch {
      return "";
    }
  }, []);

  const copyBugDevelopmentPrompt = useCallback(
    async (bug, extra = {}) => {
      const prompt = buildBugDevelopmentPrompt({
        bug,
        projectId,
        projectName: displayProjectName,
        versionName: extra.versionName,
        attachments: extra.attachments,
        currentUrl: getCurrentBugUrl(bug?.id || bug?.bug_id)
      });

      await copyText(prompt, {
        successTitle: "Geliştirme promptu hazır",
        successDescription: "Uygulama planı odaklı prompt panoya kopyalandı."
      });
    },
    [copyText, displayProjectName, getCurrentBugUrl, projectId]
  );

  const copyBugTestScenarioPrompt = useCallback(
    async (bug, extra = {}) => {
      const prompt = buildBugTestScenarioPrompt({
        bug,
        projectId,
        projectName: displayProjectName,
        versionName: extra.versionName,
        attachments: extra.attachments,
        currentUrl: getCurrentBugUrl(bug?.id || bug?.bug_id)
      });

      await copyText(prompt, {
        successTitle: "Test senaryosu hazır",
        successDescription: "İşe göre test senaryosu promptu panoya kopyalandı."
      });
    },
    [copyText, displayProjectName, getCurrentBugUrl, projectId]
  );

  const copyFormDevelopmentPrompt = useCallback(async () => {
    const versionName = (form.version || lastVersion?.version_name || "v1.0").toString();
    const prompt = buildBugDevelopmentPromptFromForm({
      form,
      projectId,
      projectName: displayProjectName,
      versionName,
      currentUrl: getCurrentBugUrl(form?.id || editingBugId)
    });

    await copyText(prompt, {
      successTitle: "Geliştirme promptu hazır",
      successDescription: isEdit
        ? "İşe göre geliştirme promptu panoya kopyalandı."
        : "Formdaki bilgilere göre geliştirme promptu panoya kopyalandı."
    });
  }, [copyText, displayProjectName, editingBugId, form, getCurrentBugUrl, isEdit, lastVersion?.version_name, projectId]);

  const copyFormTestScenarioPrompt = useCallback(async () => {
    const versionName = (form.version || lastVersion?.version_name || "v1.0").toString();
    const prompt = buildBugTestScenarioPromptFromForm({
      form,
      projectId,
      projectName: displayProjectName,
      versionName,
      currentUrl: getCurrentBugUrl(form?.id || editingBugId)
    });

    await copyText(prompt, {
      successTitle: "Test senaryosu hazır",
      successDescription: isEdit
        ? "İşe göre test senaryosu promptu panoya kopyalandı."
        : "Formdaki bilgilere göre test senaryosu promptu panoya kopyalandı."
    });
  }, [copyText, displayProjectName, editingBugId, form, getCurrentBugUrl, isEdit, lastVersion?.version_name, projectId]);

  const downloadFile = useCallback(async (url, preferredName) => {
    const u = (url || "").trim();
    if (!u) return;

    const name = (preferredName || getFileName(u) || "dosya").toString();

    try {
      const res = await fetch(u, {
        method: "GET",
        mode: "cors",
        credentials: "omit",
        cache: "no-store"
      });

      if (!res.ok) throw new Error("download_failed");

      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = name;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      window.URL.revokeObjectURL(blobUrl);

      showToast({
        title: "İndiriliyor",
        description: "Dosya indirilmeye başladı.",
        variant: "success",
        position: "bottom-right"
      });
    } catch {
      try {
        const a = document.createElement("a");
        a.href = u;
        a.download = name;
        a.target = "_blank";
        a.rel = "noreferrer";
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        showToast({
          title: "İndirme başlatıldı",
          description: "Tarayıcı dosyayı yeni sekmede indirebilir.",
          variant: "success",
          position: "bottom-right"
        });
      } catch {
        showToast({
          title: "İndirme başarısız",
          description: "Bağlantı indirilemedi.",
          variant: "error",
          position: "bottom-right"
        });
      }
    }
  }, []);

  const openAttachmentInModal = useCallback(
    (url, fallbackTitle, kindOverride) => {
      const u = (url || "").trim();
      if (!u) return;

      const kind = detectFilePreviewKind(u, kindOverride);

      const title = (fallbackTitle || getFileName(u) || getDisplaySource(u) || "Ek").toString();

      setAttViewer({
        open: true,
        url: u,
        title,
        kind
      });
    },
    []
  );

  const handleInviteUser = async (email) => {
    const value = String(email ?? form.email ?? "").trim();

    if (!value) {
      showToast({
        title: "Uyarı",
        description: "E-posta boş olamaz",
        variant: "error",
        position: "bottom-right"
      });
      return;
    }

    const copyToClipboard = async (text) => {
      const t = String(text || "").trim();
      if (!t) return;

      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(t);
        } else {
          const ta = document.createElement("textarea");
          ta.value = t;
          ta.setAttribute("readonly", "");
          ta.style.position = "fixed";
          ta.style.opacity = "0";
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }
      } catch {
      }
    };

    try {
      let res = null;

      try {
        if (typeof addUser === "function") {
          res = await addUser(value);
        }
      } catch {
        res = null;
      }

      if (!res) {
        res = await createInvite(projectId, value);
      }

      if (res?.status !== "success") {
        throw new Error(res?.message || "İşlem başarısız!");
      }

      const inviteUrl = String(res?.invite_url || "").trim();
      const addedUserEmail = String(res?.user?.email || "").trim();
      const addedUserName = String(res?.user?.name || "").trim();

      if (addedUserEmail) {
        showToast({
          title: "Kullanıcı eklendi",
          description: addedUserName ? `${addedUserName} (${addedUserEmail}) projeye eklendi.` : `${addedUserEmail} projeye eklendi.`,
          variant: "success",
          position: "bottom-right"
        });
      } else if (inviteUrl) {
        await copyToClipboard(inviteUrl);
        showToast({
          title: "Davet linki oluşturuldu",
          description: inviteUrl,
          variant: "success",
          position: "bottom-right"
        });
      } else {
        showToast({
          title: "Başarılı",
          description: res?.message || "İşlem tamamlandı.",
          variant: "success",
          position: "bottom-right"
        });
      }

      setForm((f) => ({ ...f, email: "" }));
      dispatch({ type: "user", open: false });
    } catch (e) {
      console.error("Kullanıcı ekleme/davet hatası:", e);
      showToast({
        title: "Hata",
        description: e?.message || "İşlem başarısız.",
        variant: "error",
        position: "bottom-right"
      });
    }
  };

  const handleRemoveProjectUser = async (user) => {
    const targetId = String(user?.id ?? user?.user_id ?? "").trim();

    if (!targetId) {
      showToast({
        title: "Hata",
        description: "Çıkarılacak kullanıcı bulunamadı.",
        variant: "error",
        position: "bottom-right"
      });
      return;
    }

    try {
      await removeUser(targetId);
      showToast({
        title: "Kullanıcı çıkarıldı",
        description: `${user?.name || user?.email || "Kullanıcı"} projeden çıkarıldı.`,
        variant: "success",
        position: "bottom-right"
      });
    } catch (e) {
      showToast({
        title: "Hata",
        description: e?.message || "Kullanıcı projeden çıkarılamadı.",
        variant: "error",
        position: "bottom-right"
      });
    }
  };

  const handleChangeProjectUserRole = async (user, role) => {
    const targetId = String(user?.id ?? user?.user_id ?? "").trim();

    if (!targetId) {
      showToast({
        title: "Hata",
        description: "Rolü değiştirilecek kullanıcı bulunamadı.",
        variant: "error",
        position: "bottom-right"
      });
      return;
    }

    try {
      await changeUserRole(targetId, role);
      showToast({
        title: "Rol güncellendi",
        description: `${user?.name || user?.email || "Kullanıcı"} artık ${role === "manager" ? "yönetici" : "üye"}.`,
        variant: "success",
        position: "bottom-right"
      });
    } catch (e) {
      showToast({
        title: "Hata",
        description: e?.message || "Kullanıcı rolü güncellenemedi.",
        variant: "error",
        position: "bottom-right"
      });
    }
  };

  const projectUsers = useMemo(
    () =>
      users.map((user) => {
        const isCurrentUser = isCurrentProjectUser(user, currentUserId, currentUserEmail);

        if (isCurrentUser && canManageProjectUsers && !user.isOwner && !user.isManager) {
          return {
            ...user,
            role: "manager",
            isManager: true,
            isCurrentUser
          };
        }

        return {
          ...user,
          isCurrentUser
        };
      }),
    [users, currentUserId, currentUserEmail, canManageProjectUsers]
  );

  const handleAddBug = async () => {
    const formSnapshot = { ...form };
    let tempId = "";
    let versionName = "";

    try {
      if (versions.length === 0) await createVersion("v1.0");

      versionName = (formSnapshot.version || lastVersion?.version_name || "v1.0").toString();
      const fileUrlFinal = await ensureFileUrl();
      const promptForm = {
        ...formSnapshot,
        fileUrl: fileUrlFinal,
        version: versionName
      };

      tempId = `temp-${Date.now()}`;
      const optimisticBug = {
        id: tempId,
        bug_id: tempId,
        title: formSnapshot.title,
        description: formSnapshot.description,
        file_url: fileUrlFinal || "",
        version_name: versionName,
        is_completed: false,
        created_at: new Date().toISOString(),
        email: "",
        external_url: (formSnapshot.externalUrl || "").trim() || "",
        is_saving: true
      };

      setShadowBugs((prev) => {
        const base = prev ? { ...prev } : groupByVersion(Object.values(bugs).flat());
        const key = versionName.toString();
        base[key] = base[key] ? [...base[key], optimisticBug] : [optimisticBug];
        return base;
      });

      setForm({ ...initialForm, version: versionName });
      dispatch({ type: "bug", open: false });

      const res = await createBug({
        title: formSnapshot.title,
        description: formSnapshot.description,
        file_url: fileUrlFinal,
        version_name: versionName,
        source: "web",
        external_url: (formSnapshot.externalUrl || "").trim() || undefined
      });

      const createdBug = normalizeAddedBugForPrompt({
        response: res,
        form: promptForm,
        versionName,
        fileUrl: fileUrlFinal
      });
      const createdId = createdBug.id || createdBug.bug_id || null;
	      const addedBugPrompt = buildAddedBugAiPrompt({
	        response: res,
	        form: promptForm,
	        projectId,
	        projectName: displayProjectName,
	        versionName,
	        fileUrl: fileUrlFinal,
	        currentUrl: getCurrentBugUrl(createdId)
	      });

      const selectedLabelIds = Array.isArray(formSnapshot.labels)
        ? formSnapshot.labels.map((labelId) => String(labelId).trim()).filter(Boolean)
        : [];
      const selectedLabelObjs = projectLabels
        .filter((label) => selectedLabelIds.includes(String(label.id)))
        .map((label) => ({ id: label.id, name: label.name, color: label.color }));

      const metaPriority = formSnapshot.priority || "medium";
      const metaDueDate = String(formSnapshot.dueDate || "").trim();
      const wantMeta = metaPriority !== "medium" || metaDueDate !== "";
      const optimisticMeta = wantMeta
        ? { bug_id: createdId, priority: metaPriority, due_date: metaDueDate || null }
        : null;

      if (createdId) {
        const newBug = {
          id: createdId,
          bug_id: createdBug.bug_id || createdId,
          title: createdBug.title || formSnapshot.title,
          description: createdBug.description || formSnapshot.description,
          file_url: createdBug.file_url || fileUrlFinal || "",
          version_name: createdBug.version_name || versionName,
          is_completed: createdBug.is_completed || false,
          created_at: createdBug.created_at || new Date().toISOString(),
          email: createdBug.email || "",
          external_url: createdBug.external_url || (formSnapshot.externalUrl || "").trim() || "",
          labels: selectedLabelObjs,
          meta: optimisticMeta,
          ai_prompt: addedBugPrompt
        };

        setShadowBugs((prev) => {
          const base = prev ? { ...prev } : groupByVersion(Object.values(bugs).flat());
          const key = versionName.toString();
          const list = base[key] || [];
          const replaced = list.some((item) => String(item.id) === String(tempId));
          base[key] = replaced
            ? list.map((item) => (String(item.id) === String(tempId) ? newBug : item))
            : [...list, newBug];
          return base;
        });

        try {
          localStorage.setItem(`buglog:aiPrompt:${projectId}:${createdId}`, addedBugPrompt);
          window.dispatchEvent(
            new CustomEvent("bug:aiPromptCreated", {
              detail: { projectId, bugId: createdId, prompt: addedBugPrompt }
            })
          );
        } catch {
        }
      }

      // Seçili etiketleri yeni işe bağla (best-effort) — başarısız olsa da iş eklenmiş kalır
      if (createdId && selectedLabelIds.length > 0) {
        try {
          await Promise.all(
            selectedLabelIds.map((labelId) =>
              attachLabelToBug({ bugId: createdId, labelId }).catch(() => null)
            )
          );
        } catch {
        }
      }

      // Öncelik / teslim tarihini kaydet (best-effort)
      if (createdId && wantMeta) {
        try {
          await saveBugMeta({ bugId: createdId, priority: metaPriority, dueDate: metaDueDate });
        } catch {
        }
      }

      refreshShadow().catch(() => {});

      const myUserId = String(currentUserId || readMyUserId() || "").trim();
      const myUserIdInt = myUserId ? Number(myUserId) : 0;
      const hrefWithBug = withBugParam(window.location.pathname || "/", createdId);

      if (createdId) {
        notifyProjectUsers({
	          project_id: Number(projectId),
	          actor_user_id: myUserIdInt,
	          title: formSnapshot.title ? `Yeni İş: ${formSnapshot.title}` : "Yeni iş",
	          project_name: displayProjectName || "",
	          href: hrefWithBug,
	          bug_id: createdId
	        }).catch(() => {});
      }

    } catch (e) {
      console.error("Yeni iş eklenemedi:", e);
      if (tempId && versionName) {
        setShadowBugs((prev) => {
          const base = prev ? { ...prev } : groupByVersion(Object.values(bugs).flat());
          const key = versionName.toString();
          base[key] = (base[key] || []).filter((item) => String(item.id) !== String(tempId));
          return base;
        });
      }
      showToast({
        title: "Hata",
        description: "Yeni iş eklenemedi.",
        variant: "error",
        position: "bottom-right"
      });
    }
  };


  const startEditBug = (bug) => {
    setIsEdit(true);
    setEditingBugId(bug.id);
    setForm((f) => ({
      ...f,
      id: bug.id,
      title: bug.title || "",
      description: bug.description || "",
      file: null,
      fileUrl: bug.file_url || "",
      version: bug.version_name || (lastVersion?.version_name || "v1.0"),
      externalUrl: bug.external_url || "",
      labels: Array.isArray(bug.labels)
        ? bug.labels.map((label) => String(label?.id ?? "").trim()).filter(Boolean)
        : [],
      priority: bug.meta?.priority || "medium",
      dueDate: bug.meta?.due_date ? String(bug.meta.due_date).slice(0, 10) : ""
    }));
    dispatch({ type: "bug", open: true });
  };

  const handleUpdateBug = async (draft = {}) => {
    try {
      if (!editingBugId) {
        showToast({
          title: "Hata",
          description: "Güncellenecek iş bulunamadı.",
          variant: "error",
          position: "bottom-right"
        });
        return;
      }

      const draftData = draft && typeof draft === "object" ? draft : {};
      const title = (draftData.title ?? form.title ?? "").toString().trim();
      const description = (draftData.description ?? form.description ?? "").toString();
      const externalUrl = (draftData.external_url ?? draftData.externalUrl ?? form.externalUrl ?? "")
        .toString()
        .trim();

      if (!title) {
        showToast({
          title: "Uyarı",
          description: "Başlık boş olamaz.",
          variant: "error",
          position: "bottom-right"
        });
        return;
      }

      const versionName = (
        draftData.version_name ||
        draftData.version ||
        form.version ||
        lastVersion?.version_name ||
        "v1.0"
      )
        .toString()
        .trim();

      const versionObj = versions.find(
        (v) => (v.version_name || "").toString().trim() === versionName
      );

      const uploadedFileUrl = (draftData.file_url ?? draftData.fileUrl ?? "").toString().trim();
      const fileUrlFinal = uploadedFileUrl || await ensureFileUrl();
      const actorUserId = String(currentUserId || readMyUserId() || "").trim();

      const payload = {
        title,
        description,
        file_url: fileUrlFinal || "",
        version_name: versionName,
        project_id: projectId,
        uniq_id: projectId,
        uniq: projectId,
        external_url: externalUrl || undefined,
        user_id: actorUserId || undefined,
        ...(versionObj?.id ? { version_id: versionObj.id } : {})
      };

      await updateBug(editingBugId, payload);

      // Etiketleri senkronize et (best-effort) — eklenenleri bağla, çıkarılanları kaldır
      try {
        const selectedLabelIds = Array.isArray(form.labels)
          ? form.labels.map((labelId) => String(labelId).trim()).filter(Boolean)
          : [];
        const currentLabels = await getBugLabels(editingBugId);
        const currentIds = currentLabels
          .map((label) => String(label?.id ?? "").trim())
          .filter(Boolean);
        const toAdd = selectedLabelIds.filter((labelId) => !currentIds.includes(labelId));
        const toRemove = currentIds.filter((labelId) => !selectedLabelIds.includes(labelId));

        await Promise.all([
          ...toAdd.map((labelId) =>
            attachLabelToBug({ bugId: editingBugId, labelId }).catch(() => null)
          ),
          ...toRemove.map((labelId) =>
            detachLabelFromBug({ bugId: editingBugId, labelId }).catch(() => null)
          )
        ]);
      } catch (labelErr) {
        console.error("Etiketler senkronize edilemedi:", labelErr);
      }

      // Öncelik / teslim tarihini kaydet (best-effort)
      try {
        await saveBugMeta({
          bugId: editingBugId,
          priority: form.priority || "medium",
          dueDate: String(form.dueDate || "").trim()
        });
      } catch (metaErr) {
        console.error("Öncelik/teslim tarihi kaydedilemedi:", metaErr);
      }

      await refreshShadow();

      setIsEdit(false);
      setEditingBugId(null);
      setForm({ ...initialForm, version: versionName });
      dispatch({ type: "bug", open: false });
    } catch (err) {
      console.error("Bug güncellenemedi:", err);
      showToast({
        title: "Hata",
        description: err?.message || "Güncelleme başarısız.",
        variant: "error",
        position: "bottom-right"
      });
    }
  };

  const clearCompletionGrace = useCallback((id) => {
    const key = String(id);
    if (completionTimersRef.current[key]) {
      clearTimeout(completionTimersRef.current[key]);
      delete completionTimersRef.current[key];
    }
    setCompletionGrace((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const startCompletionGrace = useCallback((id) => {
    const key = String(id);
    if (completionTimersRef.current[key]) clearTimeout(completionTimersRef.current[key]);
    setCompletionGrace((prev) => ({ ...prev, [key]: true }));
    completionTimersRef.current[key] = setTimeout(() => {
      delete completionTimersRef.current[key];
      setCompletionGrace((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }, COMPLETION_GRACE_MS);
  }, []);

  useEffect(() => {
    const timers = completionTimersRef.current;
    return () => {
      Object.values(timers).forEach((t) => clearTimeout(t));
    };
  }, []);

  const isBugSettled = useCallback(
    (bug = {}) => isBugDone(bug) && !completionGrace[String(bug.id)],
    [completionGrace]
  );

  const toggleAndRefresh = async (id, isCompleted) => {
    try {
      await toggleBug(id, isCompleted);
      await refreshShadow();
      if (isCompleted) clearCompletionGrace(id);
      else startCompletionGrace(id);
    } catch (e) {
      console.error("Durum değiştirilemedi:", e);
      showToast({
        title: "Hata",
        description: "Durum değiştirilemedi.",
        variant: "error",
        position: "bottom-right"
      });
    }
  };

  const quickError = useCallback((description) => {
    showToast({ title: "Hata", description, variant: "error", position: "bottom-right" });
  }, []);

  const loadQuickData = useCallback(async (bugId, tab) => {
    const key = String(bugId);
    setQuickBusy(true);
    try {
      let patch = null;
      if (tab === "label") patch = { labels: await getBugLabels(bugId) };
      else if (tab === "checklist") patch = { checklist: await getChecklist(bugId) };
      else if (tab === "assignee") patch = { assignees: await getAssignees(bugId) };
      else if (tab === "comment") patch = { comments: await getCommentsByBug(bugId) };
      if (patch) setQuickData((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
    } catch {
      quickError("Bilgiler alınamadı.");
    } finally {
      setQuickBusy(false);
    }
  }, [quickError]);

  const openQuickPanel = useCallback((bugId, tab) => {
    const key = String(bugId);
    if (quickPanel.bugId === key && quickPanel.tab === tab) {
      setQuickPanel({ bugId: "", tab: "" });
      return;
    }
    setQuickPanel({ bugId: key, tab });
    setQuickDraft({ checklist: "", comment: "", label: "", assignee: "" });
    loadQuickData(bugId, tab);
  }, [quickPanel, loadQuickData]);

  const runQuickAction = useCallback(async (bugId, tab, action, errorText) => {
    setQuickBusy(true);
    try {
      await action();
      await loadQuickData(bugId, tab);
      await refreshShadow();
    } catch {
      quickError(errorText);
    } finally {
      setQuickBusy(false);
    }
  }, [loadQuickData, refreshShadow, quickError]);

  const quickAddChecklistItem = (bugId) => {
    const title = String(quickDraft.checklist || "").trim();
    if (!title) return;
    setQuickDraft((prev) => ({ ...prev, checklist: "" }));
    runQuickAction(bugId, "checklist", () => addChecklistItem({ bugId, title }), "Madde eklenemedi.");
  };

  const quickToggleChecklistItem = (bugId, item) => {
    runQuickAction(
      bugId,
      "checklist",
      () => updateChecklistItem({ id: item.id, isDone: !toBool(item.is_done) }),
      "Madde güncellenemedi."
    );
  };

  const quickRemoveChecklistItem = (bugId, item) => {
    runQuickAction(bugId, "checklist", () => removeChecklistItem({ id: item.id }), "Madde silinemedi.");
  };

  const quickAttachLabel = (bugId) => {
    const labelId = String(quickDraft.label || "").trim();
    if (!labelId) return;
    setQuickDraft((prev) => ({ ...prev, label: "" }));
    runQuickAction(bugId, "label", () => attachLabelToBug({ bugId, labelId }), "Etiket eklenemedi.");
  };

  const quickDetachLabel = (bugId, labelId) => {
    runQuickAction(bugId, "label", () => detachLabelFromBug({ bugId, labelId }), "Etiket kaldırılamadı.");
  };

  const quickAssignUser = (bugId) => {
    const userId = String(quickDraft.assignee || "").trim();
    if (!userId) return;
    setQuickDraft((prev) => ({ ...prev, assignee: "" }));
    runQuickAction(bugId, "assignee", () => addAssignee({ bugId, userId }), "Kullanıcı atanamadı.");
  };

  const quickUnassignUser = (bugId, userId) => {
    runQuickAction(bugId, "assignee", () => removeAssignee({ bugId, userId }), "Atama kaldırılamadı.");
  };

  const quickAddComment = (bugId) => {
    const content = String(quickDraft.comment || "").trim();
    if (!content) return;
    setQuickDraft((prev) => ({ ...prev, comment: "" }));
    runQuickAction(
      bugId,
      "comment",
      () => createComment({ bugId, userId: currentUserId, content }),
      "Yorum eklenemedi."
    );
  };

  const removeAndRefresh = async (id, titleText) => {
    try {
      await removeBug(id);
      await refreshShadow();
      showToast({
        title: "İş silindi",
        description: `“${(titleText || "İş").toString()}” başarıyla silindi.`,
        variant: "success",
        position: "bottom-right"
      });
    } catch (e) {
      console.error("Silinemedi:", e);
      showToast({
        title: "Hata",
        description: `“${(titleText || "İş").toString()}” silinemedi.`,
        variant: "error",
        position: "bottom-right"
      });
    }
  };

	  const buildPreview = useCallback(
	    (v) =>
	      `[${displayProjectName} ${v}]\n----------------------------------\n\n${(dataBugs[v] || [])
	        .map(
	          (b) =>
	            `${b.is_completed ? "✓" : "□"} ${b.title}\n    ${htmlToText(b.description || "")}`
	        )
	        .join("\n")}`,
	    [dataBugs, displayProjectName]
	  );

  const openShare = (v) => {
    setForm((f) => ({ ...f, preview: buildPreview(v), nowVersion: v }));
    dispatch({ type: "share", open: true });
  };

  const handleShareInvite = useCallback(async () => {
    const link = `https://www.buglog.app/invite/${projectId}`;
    const text = `Sizi Buglog projeme davet ediyorum ${link}`;

    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ text });
        showToast({
          title: "Paylaşıldı",
          description: "Davet metni gönderildi.",
          variant: "success",
          position: "bottom-right"
        });
        return;
      } catch {
      }
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      showToast({
        title: "Bağlantı kopyalandı",
        description: "Davet metni panoya kopyalandı.",
        variant: "success",
        position: "bottom-right"
      });
    } catch {
      showToast({
        title: "Kopyalama başarısız",
        description: "Metni elle kopyalamanız gerekebilir.",
        variant: "error",
        position: "bottom-right"
      });
    }
  }, [projectId]);

  const openBugs = useMemo(
    () =>
      Object.values(dataBugs).reduce(
        (s, l) => s + l.filter((b) => !b.is_completed).length,
        0
      ),
    [dataBugs]
  );
  const completedBugs = useMemo(
    () =>
      Object.values(dataBugs).reduce(
        (s, l) => s + l.filter((b) => b.is_completed).length,
        0
      ),
    [dataBugs]
  );

  const versionList = (
    Object.keys(dataBugs).length
      ? Object.keys(dataBugs).map((n, i) => ({ id: i + 1, version_name: n }))
      : versions.length
        ? [...versions]
        : Object.keys(bugs).map((n) => ({ id: n, version_name: n }))
  ).sort((a, b) => {
    if (typeof a.id === "number" && typeof b.id === "number") {
      return a.id - b.id;
    }
    return String(a.version_name).localeCompare(String(b.version_name), undefined, {
      numeric: true,
      sensitivity: "base"
    });
  });

  const labelOptions = useMemo(() => {
    const map = new Map();
    Object.values(dataBugs || {}).forEach((items) => {
      (items || []).forEach((bug) => {
        (Array.isArray(bug?.labels) ? bug.labels : []).forEach((label) => {
          const labelId = String(label?.id ?? "").trim();
          if (labelId && !map.has(labelId)) {
            map.set(labelId, {
              id: labelId,
              name: label?.name || "Etiket",
              color: label?.color || "#489bf4"
            });
          }
        });
      });
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.name).localeCompare(String(b.name), "tr")
    );
  }, [dataBugs]);

  const filteredBugs = useMemo(() => {
    const query = search.trim().toLowerCase();
    const out = {};

    Object.entries(dataBugs || {}).forEach(([versionName, items]) => {
      if (versionFilter !== "all" && versionName !== versionFilter) return;

      const list = (items || []).filter((bug) => {
        const isDone = toBool(bug?.is_completed) || toBool(bug?.completed) || bug?.status === "on";
        const statusOk =
          statusFilter === "all" ||
          (statusFilter === "open" && !isDone) ||
          (statusFilter === "completed" && isDone);
        const dateOk = matchesDateFilter(bug, dateFilter);
        const queryOk = !query || getBugSearchText({ ...bug, version_name: versionName }).includes(query);
        const labelOk =
          labelFilter === "all" ||
          (Array.isArray(bug?.labels) &&
            bug.labels.some((label) => String(label?.id ?? "").trim() === labelFilter));

        return statusOk && dateOk && queryOk && labelOk;
      });

      if (list.length) {
        out[versionName] = list;
      }
    });

    return out;
  }, [dataBugs, dateFilter, search, statusFilter, versionFilter, labelFilter]);

  const filteredBugCount = useMemo(
    () => Object.values(filteredBugs).reduce((total, list) => total + list.length, 0),
    [filteredBugs]
  );

  const totalBugCount = openBugs + completedBugs;
  const hasActiveFilters =
    !!search.trim() ||
    statusFilter !== "all" ||
    versionFilter !== "all" ||
    dateFilter !== "all" ||
    labelFilter !== "all";

  const activeFilterCount =
    (statusFilter !== "all" ? 1 : 0) +
    (versionFilter !== "all" ? 1 : 0) +
    (dateFilter !== "all" ? 1 : 0) +
    (labelFilter !== "all" ? 1 : 0);

  const projectHealth = useMemo(() => {
    const allBugs = Object.values(dataBugs || {}).flat();
    const weekMs = 7 * 24 * 60 * 60 * 1000;
    const completionRate = totalBugCount
      ? Math.round((completedBugs / totalBugCount) * 100)
      : 100;

    const staleOpenCount = allBugs.filter((bug) => {
      const isDone = toBool(bug?.is_completed) || toBool(bug?.completed) || bug?.status === "on";
      const ts = getBugTimeMs(bug);
      return !isDone && ts && now - ts > weekMs;
    }).length;

    const recentActivityCount = allBugs.filter((bug) => {
      const ts = getBugTimeMs(bug);
      return ts && now - ts <= weekMs;
    }).length;

    const deadlineTs = lastVersion?.deadline ? new Date(lastVersion.deadline).getTime() : 0;
    const hasValidDeadline = deadlineTs && !Number.isNaN(deadlineTs);
    const isVersionClosed = !!lastVersion?.closed_at;
    const isOverdue = !!(hasValidDeadline && !isVersionClosed && deadlineTs < now);
    const isDueSoon = !!(
      hasValidDeadline &&
      !isVersionClosed &&
      deadlineTs >= now &&
      deadlineTs - now <= 2 * 24 * 60 * 60 * 1000
    );

    let score = 100;
    if (totalBugCount) {
      score -= Math.round((openBugs / totalBugCount) * 32);
    }
    score -= Math.min(24, staleOpenCount * 8);
    if (isOverdue) score -= 22;
    else if (isDueSoon) score -= 10;
    if (totalBugCount && recentActivityCount === 0) score -= 8;
    if (projectUsers.length === 0) score -= 6;
    if (versionList.length === 0) score -= 4;

    score = Math.max(0, Math.min(100, score));

    const tone = score >= 80 ? "good" : score >= 55 ? "watch" : "risk";
    const label = score >= 80 ? "Sağlıklı" : score >= 55 ? "Takipte" : "Riskli";
    const deadlineLabel = isVersionClosed
      ? "Kapalı"
      : isOverdue
        ? "Tarih geçti"
        : isDueSoon
          ? "Yaklaşıyor"
          : hasValidDeadline
            ? "Planlı"
            : "Tarih yok";

    let summary = "Proje dengeli ilerliyor.";
    if (!totalBugCount) {
      summary = "Henüz iş kaydı yok, ilk işler eklendikçe rapor netleşir.";
    } else if (isOverdue) {
      summary = "Sürüm tarihi geçmiş, açık işleri hızlıca gözden geçirmek iyi olur.";
    } else if (staleOpenCount > 0) {
      summary = `${staleOpenCount} açık iş 7 günü geçti, önceliklendirme gerekebilir.`;
    } else if (openBugs === 0) {
      summary = "Tüm işler tamamlanmış görünüyor.";
    } else if (completionRate >= 70) {
      summary = "Tamamlanma oranı iyi, kalan açık işler kontrol altında.";
    } else if (recentActivityCount === 0) {
      summary = "Son 7 günde hareket yok, proje akışı durgun görünüyor.";
    }

    return {
      score,
      label,
      tone,
      completionRate,
      staleOpenCount,
      recentActivityCount,
      deadlineLabel,
      summary
    };
  }, [
    completedBugs,
    dataBugs,
    lastVersion,
    now,
    openBugs,
    projectUsers.length,
    totalBugCount,
    versionList.length
  ]);

  const filteredVersionList = useMemo(
    () => versionList.filter((ver) => (filteredBugs[ver.version_name] || []).length > 0),
    [filteredBugs, versionList]
  );

  const currentFilterSnapshot = useMemo(
    () => ({
      search,
      statusFilter,
      versionFilter,
      dateFilter,
      labelFilter
    }),
    [dateFilter, search, statusFilter, versionFilter, labelFilter]
  );

  const persistSavedFilters = useCallback((nextFilters) => {
    const next = (Array.isArray(nextFilters) ? nextFilters : []).slice(0, MAX_SAVED_FILTERS);
    setSavedFilters(next);
    writeSavedBugFilters(projectId, next);
  }, [projectId]);

  const applySavedBugFilter = useCallback((filter) => {
    if (!filter) return;

    setSearch(filter.search || "");
    setStatusFilter(filter.statusFilter || "all");
    setVersionFilter(filter.versionFilter || "all");
    setDateFilter(filter.dateFilter || "all");
    setLabelFilter(filter.labelFilter || "all");
    setSavedFilterName(filter.name || "");
    setActiveSavedFilterId(filter.id || "");
    setSelectedBugIds([]);

    showToast({
      title: "Filtre uygulandı",
      description: `${filter.name} görünümü açıldı.`,
      position: "bottom-right"
    });
  }, []);

  const saveCurrentBugFilter = useCallback(() => {
    const name = String(savedFilterName || "").trim();

    if (!name) {
      showToast({
        title: "Filtre adı gerekli",
        description: "Kaydetmeden önce kısa bir ad yaz.",
        variant: "error",
        position: "bottom-right"
      });
      return;
    }

    const nameKey = name.toLocaleLowerCase("tr-TR");
    const existingIndex = savedFilters.findIndex(
      (filter) => String(filter.name || "").trim().toLocaleLowerCase("tr-TR") === nameKey
    );
    const previous = existingIndex >= 0 ? savedFilters[existingIndex] : null;
    const nextFilter = {
      id: previous?.id || `filter-${Date.now()}`,
      name,
      ...currentFilterSnapshot,
      createdAt: previous?.createdAt || Date.now()
    };

    const next = existingIndex >= 0
      ? savedFilters.map((filter, index) => (index === existingIndex ? nextFilter : filter))
      : [nextFilter, ...savedFilters];

    persistSavedFilters(next);
    setActiveSavedFilterId(nextFilter.id);

    showToast({
      title: existingIndex >= 0 ? "Filtre güncellendi" : "Filtre kaydedildi",
      description: `${name} artık hızlıca seçilebilir.`,
      position: "bottom-right"
    });
  }, [currentFilterSnapshot, persistSavedFilters, savedFilterName, savedFilters]);

  const deleteActiveSavedFilter = useCallback(() => {
    if (!activeSavedFilterId) return;

    const target = savedFilters.find((filter) => filter.id === activeSavedFilterId);
    const next = savedFilters.filter((filter) => filter.id !== activeSavedFilterId);
    persistSavedFilters(next);
    setActiveSavedFilterId("");
    setSavedFilterName("");

    showToast({
      title: "Kayıtlı filtre silindi",
      description: target?.name ? `${target.name} kaldırıldı.` : "Filtre kaldırıldı.",
      position: "bottom-right"
    });
  }, [activeSavedFilterId, persistSavedFilters, savedFilters]);

  const clearBugFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setVersionFilter("all");
    setDateFilter("all");
    setLabelFilter("all");
    setActiveSavedFilterId("");
    setSavedFilterName("");
  };

  const versionSig = useMemo(() => {
    try {
      return versionList
        .map((v) => `${String(v.id || "")}:${String(v.version_name || "")}`)
        .join("|");
    } catch {
      return String(Date.now());
    }
  }, [versionList]);

  useEffect(() => {
    const basePath = (() => {
      try {
        return (window.location.pathname || "").split("?")[0];
      } catch {
        return "";
      }
    })();

    const idx = readGlobalVersionIndex();
    let changed = false;

    const next = {
      byVersionKey: { ...(idx.byVersionKey || {}) },
      byId: { ...(idx.byId || {}) }
    };

    for (const ver of versionList) {
      const k = buildGlobalVersionKey(projectId, ver);
      if (!k) continue;

      const prevStoredRaw = next.byVersionKey[k];
      const prevStored = String(prevStoredRaw || "").trim();
      const prevDigits = getUidDigits(prevStored);
      const prevUid = prevDigits ? `${VERSION_UID_PREFIX}${prevDigits}` : "";

      const needNew = !isVersionUid(prevUid) || isLegacyPrefixedId(prevStored);

      let uid = prevUid;

      if (needNew) {
        uid = genVersionUid(next.byId);
        next.byVersionKey[k] = uid;
        changed = true;
      } else {
        if (prevStored !== uid) {
          next.byVersionKey[k] = uid;
          changed = true;
        }
      }

      const prevRec = next.byId[uid] && typeof next.byId[uid] === "object" ? next.byId[uid] : null;

	      const rec = {
	        id: uid,
	        versionName: String(ver.version_name || ""),
	        projectName: String(displayProjectName || ""),
	        path: String(basePath || ""),
	        projectKey: String(k || "")
	      };

      if (!prevRec) {
        next.byId[uid] = rec;
        changed = true;
      } else {
        const same =
          String(prevRec.versionName || "") === rec.versionName &&
          String(prevRec.projectName || "") === rec.projectName &&
          String(prevRec.path || "") === rec.path &&
          String(prevRec.projectKey || "") === rec.projectKey;

        if (!same) {
          next.byId[uid] = { ...prevRec, ...rec };
          changed = true;
        }
      }
    }

    if (changed) {
      writeGlobalVersionIndex(next);
      setVersionUidTick((x) => x + 1);
      try {
        window.dispatchEvent(new CustomEvent("versionUid:changed", { detail: { projectId } }));
      } catch {
      }
    }
	  }, [displayProjectName, projectId, versionSig]);

  const versionUidByName = useMemo(() => {
    const idx = readGlobalVersionIndex();
    const out = {};
    for (const ver of versionList) {
      const k = buildGlobalVersionKey(projectId, ver);
      if (!k) continue;

      const raw = idx.byVersionKey?.[k];
      const uid = normalizeVersionUid(raw);

      if (isVersionUid(uid)) out[String(ver.version_name || "")] = uid;
    }
    return out;
  }, [projectId, versionSig, versionUidTick]);

  useEffect(() => {
    try {
      const qs = new URLSearchParams(window.location.search || "");
      const vidRaw = qs.get("vid") || qs.get("id") || "";
      const uid = normalizeVersionUid(vidRaw);
      const vname = String(qs.get("ver") || "").trim();

      let targetUid = isVersionUid(uid) ? uid : "";

      if (!targetUid && vname) {
        const found = versionList.find(
          (v) => String(v.version_name || "").toLowerCase() === vname.toLowerCase()
        );
        if (found) {
          const idx = readGlobalVersionIndex();
          const k = buildGlobalVersionKey(projectId, found);
          const raw = idx.byVersionKey?.[k] || "";
          const nuid = normalizeVersionUid(raw);
          if (isVersionUid(nuid)) targetUid = nuid;
        }
      }

      if (targetUid) {
        const el = document.getElementById(`version-${targetUid}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    } catch {
    }
  }, [projectId, versionSig, versionUidTick]);

  const saveDeadline = async (iso) => {
    if (!lastVersion) return;
    patchVersionMeta(projectId, lastVersion, { deadline: iso ?? "" });
    setVersionTick((x) => x + 1);
    setDeadlineDlg({ open: false, id: null, date: "" });
  };

  const closeVersion = async () => {
    if (!lastVersion) return;
    patchVersionMeta(projectId, lastVersion, { closed_at: new Date().toISOString() });
    setVersionTick((x) => x + 1);
  };

  const reopenVersion = async () => {
    if (!lastVersion) return;
    patchVersionMeta(projectId, lastVersion, { closed_at: "" });
    setVersionTick((x) => x + 1);
  };

  const headerButtons = [
    token === "11771177"
      ? {
        icon: <FiLogIn />,
        label: "Giriş Yap",
        onClick: () => navigate("/login")
      }
      : {
        icon: <FiUserPlus />,
        label: "Kullanıcılar",
        onClick: () => dispatch({ type: "user", open: true })
      },
    {
      icon: <img src="/branch.svg" alt="Yeni Sürüm" height={15} />,
      label: "Yeni Sürüm",
      onClick: () => dispatch({ type: "version", open: true })
    },
    {
      icon: <FiMoreHorizontal />,
      onClick: () => dispatch({ type: "info", open: true })
    }
  ];

  const [expanded, setExpanded] = useState({});
  const toggleDetails = useCallback((id) => setExpanded((s) => ({ ...s, [id]: !s[id] })), []);

  const [attachmentsMap, setAttachmentsMap] = useState({});
  const fetchedIdsRef = useRef(new Set());
  const lastViewedIdRef = useRef(null);

  const filteredBugEntries = useMemo(
    () =>
      versionList.flatMap((ver) => {
        const versionName = ver.version_name;
        return (filteredBugs[versionName] || [])
          .map((bug) => ({
            key: getBugKey(bug),
            bug,
            versionName
          }))
          .filter((entry) => entry.key);
      }),
    [filteredBugs, versionList]
  );

  const allBugEntries = useMemo(
    () =>
      Object.entries(dataBugs || {}).flatMap(([versionName, list]) =>
        (list || [])
          .map((bug) => ({
            key: getBugKey(bug),
            bug,
            versionName
          }))
          .filter((entry) => entry.key)
      ),
    [dataBugs]
  );

  const allBugEntryById = useMemo(() => {
    const map = new Map();
    allBugEntries.forEach((entry) => {
      map.set(entry.key, entry);
    });
    return map;
  }, [allBugEntries]);

  const selectedBugIdSet = useMemo(() => new Set(selectedBugIds), [selectedBugIds]);
  const visibleBugIds = useMemo(() => filteredBugEntries.map((entry) => entry.key), [filteredBugEntries]);
  const selectedVisibleCount = useMemo(
    () => visibleBugIds.filter((id) => selectedBugIdSet.has(id)).length,
    [selectedBugIdSet, visibleBugIds]
  );
  const allVisibleSelected = visibleBugIds.length > 0 && selectedVisibleCount === visibleBugIds.length;
  const selectedBugEntries = useMemo(
    () => selectedBugIds.map((id) => allBugEntryById.get(id)).filter(Boolean),
    [allBugEntryById, selectedBugIds]
  );

  useEffect(() => {
    setSelectedBugIds((prev) => {
      const next = prev.filter((id) => allBugEntryById.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [allBugEntryById]);

  const toggleSelectedBug = useCallback((bug) => {
    const key = getBugKey(bug);
    if (!key) return;

    setSelectedBugIds((prev) =>
      prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key]
    );
  }, []);

  const toggleSelectVisibleBugs = useCallback(() => {
    setSelectedBugIds((prev) => {
      if (!visibleBugIds.length) return prev;

      const visibleSet = new Set(visibleBugIds);
      if (allVisibleSelected) {
        return prev.filter((id) => !visibleSet.has(id));
      }

      const next = [...prev];
      visibleBugIds.forEach((id) => {
        if (!next.includes(id)) next.push(id);
      });
      return next;
    });
  }, [allVisibleSelected, visibleBugIds]);

  const clearSelectedBugs = useCallback(() => {
    setSelectedBugIds([]);
  }, []);

  const buildBugCsvRows = useCallback(
    (entries) =>
      entries.map(({ bug, versionName }) => {
        const bugKey = getBugKey(bug);
        const attachments = attachmentsMap[bugKey] || attachmentsMap[bug.id] || [];
        return [
          displayProjectName,
          versionName,
          bug.title || "",
          htmlToText(bug.description || ""),
          isBugDone(bug) ? "Tamamlandı" : "Açık",
          bug.email || "",
          csvDate(bug.created_at || bug.createdAt || bug.created || ""),
          csvDate(bug.updated_at || bug.updatedAt || bug.updated || ""),
          bug.file_url || "",
          bug.external_url || "",
          attachments.length
        ];
      }),
    [attachmentsMap, displayProjectName]
  );

  const downloadBugEntriesCsv = useCallback(
    (entries, options = {}) => {
      const rows = buildBugCsvRows(entries || []);

      if (!rows.length) {
        showToast({
          title: "CSV oluşturulamadı",
          description: options.emptyDescription || "Dışa aktarılacak iş bulunamadı.",
          variant: "error",
          position: "bottom-right"
        });
        return;
      }

      const headers = [
        "Proje",
        "Sürüm",
        "Başlık",
        "Açıklama",
        "Durum",
        "Oluşturan",
        "Oluşturulma",
        "Güncellenme",
        "Dosya",
        "Bağlantı",
        "Ek Sayısı"
      ];

      const csv = [
        headers.map(csvCell).join(","),
        ...rows.map((row) => row.map(csvCell).join(","))
      ].join("\n");

      const baseName = csvFileName(displayProjectName)
        .replace(/-isler\.csv$/i, "")
        .replace(/\.csv$/i, "");
      const suffix = options.suffix || "isler";
      const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${baseName}-${suffix}.csv`;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      showToast({
        title: "CSV hazır",
        description: `${rows.length} iş dışa aktarıldı.`,
        variant: "success",
        position: "bottom-right"
      });
    },
    [buildBugCsvRows, displayProjectName]
  );

  const exportBugsCsv = useCallback(() => {
    downloadBugEntriesCsv(filteredBugEntries, {
      emptyDescription: "Dışa aktarılacak iş bulunamadı.",
      suffix: "isler"
    });
  }, [downloadBugEntriesCsv, filteredBugEntries]);

  const exportSelectedBugsCsv = useCallback(() => {
    downloadBugEntriesCsv(selectedBugEntries, {
      emptyDescription: "Dışa aktarılacak seçili iş bulunamadı.",
      suffix: "secilen-isler"
    });
  }, [downloadBugEntriesCsv, selectedBugEntries]);

  const copySelectedDevelopmentPrompts = useCallback(async () => {
    if (!selectedBugEntries.length) return;

    const prompt = selectedBugEntries
      .map(({ bug, versionName }, index) =>
        buildBugDevelopmentPrompt({
          bug,
          projectId,
          projectName: displayProjectName,
          versionName,
          attachments: attachmentsMap[getBugKey(bug)] || attachmentsMap[bug.id] || [],
          currentUrl: getCurrentBugUrl(bug?.id || bug?.bug_id),
          prefix: `#${index + 1}`
        })
      )
      .join("\n\n---\n\n");

    await copyText(prompt, {
      successTitle: "Toplu geliştirme promptu hazır",
      successDescription: `${selectedBugEntries.length} iş için geliştirme promptu panoya kopyalandı.`
    });
  }, [
    attachmentsMap,
    copyText,
    displayProjectName,
    getCurrentBugUrl,
    projectId,
    selectedBugEntries
  ]);

  const copySelectedTestScenarioPrompts = useCallback(async () => {
    if (!selectedBugEntries.length) return;

    const prompt = selectedBugEntries
      .map(({ bug, versionName }, index) =>
        buildBugTestScenarioPrompt({
          bug,
          projectId,
          projectName: displayProjectName,
          versionName,
          attachments: attachmentsMap[getBugKey(bug)] || attachmentsMap[bug.id] || [],
          currentUrl: getCurrentBugUrl(bug?.id || bug?.bug_id),
          prefix: `#${index + 1}`
        })
      )
      .join("\n\n---\n\n");

    await copyText(prompt, {
      successTitle: "Toplu test senaryosu hazır",
      successDescription: `${selectedBugEntries.length} iş için test senaryosu promptu panoya kopyalandı.`
    });
  }, [
    attachmentsMap,
    copyText,
    displayProjectName,
    getCurrentBugUrl,
    projectId,
    selectedBugEntries
  ]);

  const completeSelectedBugs = useCallback(async () => {
    const targets = selectedBugEntries.filter(({ bug }) => !isBugDone(bug) && !bug?.is_saving);

    if (!targets.length) {
      showToast({
        title: "Tamamlanacak iş yok",
        description: "Seçili işlerin tamamı zaten tamamlanmış.",
        variant: "error",
        position: "bottom-right"
      });
      return;
    }

    setBulkBusy(true);
    try {
      for (const { bug } of targets) {
        await toggleBug(bug.id || bug.bug_id, false);
      }
      await refreshShadow();
      showToast({
        title: "Toplu işlem tamamlandı",
        description: `${targets.length} iş tamamlandı olarak işaretlendi.`,
        variant: "success",
        position: "bottom-right"
      });
      setSelectedBugIds([]);
    } catch (error) {
      console.error("Toplu tamamlama başarısız:", error);
      showToast({
        title: "Hata",
        description: "Seçili işler tamamlanamadı.",
        variant: "error",
        position: "bottom-right"
      });
    } finally {
      setBulkBusy(false);
    }
  }, [refreshShadow, selectedBugEntries, toggleBug]);

  const deleteSelectedBugs = useCallback(async () => {
    const targets = selectedBugEntries.filter(({ bug }) => !bug?.is_saving);

    if (!targets.length) {
      setBulkDeleteDlg(false);
      return;
    }

    setBulkBusy(true);
    try {
      for (const { bug } of targets) {
        await removeBug(bug.id || bug.bug_id);
      }
      await refreshShadow();
      showToast({
        title: "Toplu silme tamamlandı",
        description: `${targets.length} iş silindi.`,
        variant: "success",
        position: "bottom-right"
      });
      setSelectedBugIds([]);
      setBulkDeleteDlg(false);
    } catch (error) {
      console.error("Toplu silme başarısız:", error);
      showToast({
        title: "Hata",
        description: "Seçili işler silinemedi.",
        variant: "error",
        position: "bottom-right"
      });
    } finally {
      setBulkBusy(false);
    }
  }, [refreshShadow, removeBug, selectedBugEntries]);

  const fetchBugAttachments = useCallback(async (bugId) => {
    try {
      const data = await getBugDetails(bugId);
      const list = Array.isArray(data?.attachments) ? data.attachments : [];
      setAttachmentsMap((prev) => ({ ...prev, [bugId]: list }));
      fetchedIdsRef.current.add(bugId);
    } catch {
    }
  }, []);

  useEffect(() => {
    const all = Object.values(dataBugs || {}).flat();
    const ids = all.map((b) => b.id).filter(Boolean);
    const toFetch = ids.filter((i) => !fetchedIdsRef.current.has(i)).slice(0, 40);

    let cancelled = false;
    (async () => {
      for (const bid of toFetch) {
        if (cancelled) return;
        await fetchBugAttachments(bid);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dataBugs, fetchBugAttachments]);

  useEffect(() => {
    if (detailId) {
      lastViewedIdRef.current = detailId;
    } else if (lastViewedIdRef.current) {
      fetchBugAttachments(lastViewedIdRef.current);
    }
  }, [detailId, fetchBugAttachments]);

  return (
    <div>
      <Header buttons={headerButtons} />
      <div className="layout">
        <button
          type="button"
          className="back-btn back-btn--outside"
          aria-label="Geri dön"
          title="Dashboard'a dön"
          onClick={() => navigate("/")}
        >
          <FiChevronLeft size={18} />
        </button>

        <aside className="sidebar">
          <div className="sidebar-header">
            <div
              className={`avatar ${cover ? "has-cover" : ""}`}
              style={
                cover
                  ? { backgroundImage: `url(${cover})` }
                  : color
                    ? { background: color, color: "#fff" }
                    : undefined
              }
            >
              {!cover && initials}
            </div>
            <div className="sidebar-title-wrap">
              <div className="sidebar-title-row">
	                <h2 className="sidebar-title">{displayProjectName}</h2>
              </div>

              <div className="sidebar-actions">
                <button
                  onClick={() => dispatch({ type: "info", open: true })}
                  className="sidebar-desc"
                >
                  Detay
                </button>
                <button
                  onClick={() => setEditModalOpen(true)}
                  className="sidebar-desc"
                  style={{ marginLeft: 8 }}
                >
                  Düzenle
                </button>
              </div>
            </div>
          </div>

          <section className="project-panel">
            <div className="project-panel-head">
              <div>
                <h3>Projeler</h3>
                <p>Projeyi buradan değiştir.</p>
              </div>
              <button
                type="button"
                className={`project-panel-toggle${projectPanelOpen ? " open" : ""}`}
                onClick={() => setProjectPanelOpen((prev) => !prev)}
                aria-expanded={projectPanelOpen}
              >
                <span>{projectPanelOpen ? "Listeyi Gizle" : "Listeyi Aç"}</span>
                <FiChevronDown size={16} />
              </button>
            </div>

            <div className="project-panel-current">
              <span>Aktif Proje</span>
	              <strong>{displayProjectName}</strong>
            </div>

            {projectPanelOpen && (
              <>
                <label className="project-panel-sort" htmlFor="sidebar-project-sort">
                  <span>Sırala</span>
                  <select
                    id="sidebar-project-sort"
                    value={projectSort}
                    onChange={(e) => setProjectSort(e.target.value)}
                  >
                    <option value="az">A-Z</option>
                    <option value="za">Z-A</option>
                  </select>
                </label>

                <div className="project-panel-list">
                  {projectOptionsLoading ? (
                    <div className="project-panel-empty">Projeler yükleniyor...</div>
                  ) : sortedProjectOptions.length === 0 ? (
                    <div className="project-panel-empty">Görüntülenecek proje bulunamadı.</div>
                  ) : (
                    sortedProjectOptions.map((project) => {
                      const itemId = String(project?.uniq || project?.id || "").trim();
                      const itemName = getProjectDisplayName(project) || "Proje";
                      const isCurrent = itemId === String(projectId || "").trim();

                      return (
                        <button
                          key={itemId}
                          type="button"
                          className={`project-panel-item${isCurrent ? " active" : ""}`}
                          onClick={() => !isCurrent && handleProjectSwitch(project)}
                          disabled={isCurrent}
                        >
                          <div className="project-panel-copy">
                            <strong>{itemName}</strong>
                            <span>{isCurrent ? "Şu an açık proje" : "Geçiş yapmak için tıkla"}</span>
                          </div>
                          <span className="project-panel-state">
                            {isCurrent ? "Aktif" : "Aç"}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </section>

          {lastVersion && (
            <div className="version-box">
              <div className="version-box-title">
                {lastVersion.version_name}
                {lastVersion.closed_at ? <FiCheck /> : <FiAlertTriangle color="#FFCC00" />}
              </div>

              {lastVersion.closed_at ? (
                <>
                  <p className="deadline">
                    <FiCheck size={13} style={{ marginRight: 4 }} />
                    {new Date(lastVersion.closed_at).toLocaleDateString("tr-TR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric"
                    })}{" "}
                    · Kapatıldı
                  </p>

                  {lastVersion.deadline ? (
                    <p className="deadline" style={{ marginTop: 0 }}>
                      <FiClock size={13} style={{ marginRight: 4 }} />
                      {new Date(lastVersion.deadline).toLocaleDateString("tr-TR", {
                        day: "numeric",
                        month: "long",
                        year: "numeric"
                      })}{" "}
                      · Yayın tarihi
                    </p>
                  ) : null}

                  <button
                    className="close-version-btn secondary"
                    onClick={() => setReopenOpen(true)}
                  >
                    Sürümü Yeniden Aç
                  </button>
                </>
              ) : (
                <>
                  {lastVersion.deadline ? (
                    <>
                      <p className="deadline">
                        <FiClock size={13} style={{ marginRight: 4 }} />
                        {new Date(lastVersion.deadline).toLocaleDateString("tr-TR", {
                          day: "numeric",
                          month: "long",
                          year: "numeric"
                        })}{" "}
                        ·{" "}
                        <span className={`count ${countdownInfo?.level}`}>{countdownInfo?.text}</span>
                      </p>
                      <div className="bar">
                        <div style={{ width: `${countdownInfo?.pct || 0}%` }} />
                      </div>
                      <div className="btn-row">
                        <button
                          className="close-version-btn secondary"
                          onClick={() =>
                            setDeadlineDlg({
                              open: true,
                              id: lastVersion.id,
                              date: lastVersion.deadline
                            })
                          }
                        >
                          Düzenle
                        </button>
                        <button
                          className="close-version-btn secondary danger"
                          onClick={() => saveDeadline(null)}
                        >
                          <FiX size={14} /> Sıfırla
                        </button>
                      </div>
                    </>
                  ) : (
                    <button
                      className="close-version-btn secondary"
                      onClick={() => setDeadlineDlg({ open: true, id: lastVersion.id, date: "" })}
                    >
                      Yayın Tarihi Ayarla
                    </button>
                  )}

                  <button className="close-version-btn" onClick={() => setCompleteOpen(true)}>
                    Sürümü Tamamla
                  </button>
                </>
              )}
            </div>
          )}

          <ul className="sidebar-stats">
            <li className="stat-item">
              <span className="stat-number">{versionList.length}</span>
              <span className="stat-label">Sürüm</span>
            </li>
            <li className="stat-item">
              <span className="stat-number">{openBugs + completedBugs}</span>
              <span className="stat-label">İş</span>
            </li>
            <li className="stat-item">
              <span className="stat-number">{completedBugs}</span>
              <span className="stat-label">Tamamlandı</span>
            </li>
            <li className="stat-item">
              <span className="stat-number">{openBugs}</span>
              <span className="stat-label">Açık</span>
            </li>
          </ul>

          <section className={`project-health-card ${projectHealth.tone}`}>
            <div className="project-health-head">
              <div>
                <span className="project-health-kicker">
                  <FiActivity size={13} />
                  Proje Sağlık Raporu
                </span>
                <h3>{projectHealth.label}</h3>
              </div>

              <div className="project-health-score">
                <strong>{projectHealth.score}</strong>
                <span>/100</span>
              </div>
            </div>

            <div className="project-health-bar" aria-hidden="true">
              <span style={{ width: `${projectHealth.score}%` }} />
            </div>

            <p className="project-health-summary">{projectHealth.summary}</p>

            <div className="project-health-grid">
              <div className="project-health-metric">
                <span>Tamamlanma</span>
                <strong>{projectHealth.completionRate}%</strong>
              </div>
              <div className="project-health-metric">
                <span>Son 7 gün</span>
                <strong>{projectHealth.recentActivityCount}</strong>
              </div>
              <div className="project-health-metric">
                <span>Bekleyen</span>
                <strong>{projectHealth.staleOpenCount}</strong>
              </div>
              <div className="project-health-metric">
                <span>Sürüm</span>
                <strong>{projectHealth.deadlineLabel}</strong>
              </div>
            </div>
          </section>

	          <h3 className="sidebar-sub">
	            Kullanıcılar
	            <button onClick={() => dispatch({ type: "user", open: true })}>
	              {canManageProjectUsers ? "Yönet" : "Görüntüle"}
	            </button>
	            {canManageProjectUsers ? (
	              <button onClick={handleShareInvite}>Paylaş</button>
	            ) : null}
	          </h3>

          <ul className="sidebar-users">
            {projectUsers.map((u, index) => (
              <li
                key={u.id || u.email || `${u.name}-${index}`}
                className="sidebar-user-item"
              >
                <div className="sidebar-user-main">
                  <span className="dot" />
                  <div className="sidebar-user-copy">
                    <span className="sidebar-user-name">{u.name}</span>
                    {u.email ? (
                      <span className="sidebar-user-email">{u.email}</span>
                    ) : null}
                  </div>
                </div>

                <div className="sidebar-user-controls">
                  <div className="sidebar-user-badges">
                    <span className={`sidebar-user-role ${u.role || "member"}`}>
                      {getProjectUserRoleLabel(u.role)}
                    </span>
                    {u.isCurrentUser ? (
                      <span className="sidebar-user-self">Sen</span>
                    ) : null}
                  </div>

                  {canManageProjectUsers && !u.isOwner && !u.isCurrentUser ? (
                    <button
                      type="button"
                      className="sidebar-user-remove"
                      onClick={() => handleRemoveProjectUser(u)}
                    >
                      Çıkar
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
            {!projectUsers.length && <li style={{ opacity: 0.3 }}>Kullanıcı bulunamadı</li>}
          </ul>
        </aside>

        <div className="bugs-container">
          <header className="bugs-header">
            <div className="task-search">
              <FiSearch size={16} />
              <input
                type="text"
                placeholder="Başlık, açıklama, ek veya kişi içinde ara..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setActiveSavedFilterId("");
                }}
              />
            </div>

            <button
              type="button"
              className={`task-filter-toggle${filtersOpen ? " open" : ""}`}
              onClick={() => setFiltersOpen((v) => !v)}
            >
              <FiSliders size={15} />
              <span>Filtrele</span>
              {activeFilterCount > 0 ? (
                <span className="task-filter-count">{activeFilterCount}</span>
              ) : null}
            </button>

            {filteredBugCount > 0 ? (
              <button
                type="button"
                className="csv-export-button"
                onClick={exportBugsCsv}
                title="CSV Export"
              >
                <FiDownload size={15} />
              </button>
            ) : null}

            <button
              onClick={() => {
                setForm((prev) => ({
                  ...initialForm,
                  version: lastVersion?.version_name || prev.version || "v1.0"
                }));
                setIsEdit(false);
                setEditingBugId(null);
                dispatch({ type: "bug", open: true });
              }}
              className="add-bug-button"
            >
              <FiPlusCircle size={16} />
              <span className="add-bug-text">Yeni İş</span>
            </button>
          </header>

          <div className={`task-filter-panel${filtersOpen ? " open" : ""}`}>
            <div className="task-filter-controls">
              <label className="task-filter-field">
                <span>Durum</span>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setActiveSavedFilterId("");
                  }}
                >
                  <option value="all">Tüm işler</option>
                  <option value="open">Açık işler</option>
                  <option value="completed">Tamamlananlar</option>
                </select>
              </label>

              <label className="task-filter-field">
                <span>Sürüm</span>
                <select
                  value={versionFilter}
                  onChange={(e) => {
                    setVersionFilter(e.target.value);
                    setActiveSavedFilterId("");
                  }}
                >
                  <option value="all">Tüm sürümler</option>
                  {versionList.map((ver) => (
                    <option key={ver.id || ver.version_name} value={ver.version_name}>
                      {ver.version_name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="task-filter-field">
                <span>Tarih</span>
                <select
                  value={dateFilter}
                  onChange={(e) => {
                    setDateFilter(e.target.value);
                    setActiveSavedFilterId("");
                  }}
                >
                  <option value="all">Tüm tarihler</option>
                  <option value="today">Bugün</option>
                  <option value="last7">Son 7 gün</option>
                  <option value="last30">Son 30 gün</option>
                </select>
              </label>

              {labelOptions.length > 0 && (
                <label className="task-filter-field">
                  <span>Etiket</span>
                  <select
                    value={labelFilter}
                    onChange={(e) => {
                      setLabelFilter(e.target.value);
                      setActiveSavedFilterId("");
                    }}
                  >
                    <option value="all">Tüm etiketler</option>
                    {labelOptions.map((label) => (
                      <option key={label.id} value={label.id}>
                        {label.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>

            <div className="saved-filter-controls">
              <label className="task-filter-field saved-filter-select">
                <span>Kayıtlı Filtre</span>
                <select
                  value={activeSavedFilterId}
                  onChange={(e) => {
                    const nextId = e.target.value;
                    if (!nextId) {
                      setActiveSavedFilterId("");
                      return;
                    }
                    applySavedBugFilter(savedFilters.find((filter) => filter.id === nextId));
                  }}
                >
                  <option value="">Filtre seç</option>
                  {savedFilters.map((filter) => (
                    <option key={filter.id} value={filter.id}>
                      {filter.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="task-filter-field saved-filter-name">
                <span>Filtre Adı</span>
                <input
                  type="text"
                  value={savedFilterName}
                  onChange={(e) => setSavedFilterName(e.target.value)}
                  placeholder="Örn: Açık işler"
                />
              </label>

              <div className="saved-filter-buttons">
                <button
                  type="button"
                  className="save-filter-button"
                  onClick={saveCurrentBugFilter}
                  disabled={!String(savedFilterName || "").trim()}
                  title="Mevcut filtreyi kaydet"
                >
                  <FiSave size={15} />
                  <span>Kaydet</span>
                </button>
                {activeSavedFilterId ? (
                  <button
                    type="button"
                    className="delete-filter-button"
                    onClick={deleteActiveSavedFilter}
                    title="Kayıtlı filtreyi sil"
                  >
                    <FiTrash size={15} />
                  </button>
                ) : null}
              </div>
            </div>

          </div>

          <div className="task-filter-summary">
            <strong>{filteredBugCount}</strong>
            <span>/ {totalBugCount} iş</span>
            {hasActiveFilters ? (
              <button type="button" onClick={clearBugFilters}>
                <FiX size={14} />
                <span>Temizle</span>
              </button>
            ) : null}
          </div>

          {filteredBugCount > 0 && (
            <div className={`bulk-actions${selectMode ? " active" : ""}`}>
              {selectMode ? (
                <label className={`bulk-select-all${allVisibleSelected ? " checked" : ""}`}>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectVisibleBugs}
                    disabled={bulkBusy || !visibleBugIds.length}
                  />
                  <span />
                </label>
              ) : null}

              <div className="bulk-actions-copy">
                <strong>
                  {selectMode
                    ? selectedBugEntries.length
                      ? `${selectedBugEntries.length} iş seçili`
                      : "Seçim modu"
                    : "Toplu İşlemler"}
                </strong>
                <small>
                  {selectMode
                    ? selectedBugEntries.length
                      ? `${selectedVisibleCount} tanesi bu görünümde`
                      : "Kartlardaki kutulardan işleri seç"
                    : "Birden fazla işi birlikte yönetmek için seçim moduna geç"}
                </small>
              </div>

              <div className="bulk-actions-buttons">
                {selectMode ? (
                  <>
                    {selectedBugEntries.length > 0 ? (
                      <>
                    <button type="button" onClick={exportSelectedBugsCsv} disabled={bulkBusy}>
                      <FiDownload size={15} />
                      <span>CSV</span>
                    </button>
                    <button type="button" onClick={copySelectedDevelopmentPrompts} disabled={bulkBusy}>
                      <FiActivity size={15} />
                      <span>Geliştir</span>
                    </button>
                    <button type="button" onClick={copySelectedTestScenarioPrompts} disabled={bulkBusy}>
                      <FiCheck size={15} />
                      <span>Test</span>
                    </button>
                    <button type="button" onClick={completeSelectedBugs} disabled={bulkBusy}>
                      <FiCheck size={15} />
                      <span>Tamamla</span>
                    </button>
                    <button
                      type="button"
                      className="danger"
                      onClick={() => setBulkDeleteDlg(true)}
                      disabled={bulkBusy}
                    >
                      <FiTrash size={15} />
                      <span>Sil</span>
                    </button>
                      </>
                    ) : null}
                    <button
                      type="button"
                      className="ghost"
                      onClick={() => {
                        clearSelectedBugs();
                        setSelectMode(false);
                      }}
                      disabled={bulkBusy}
                    >
                      <FiX size={15} />
                      <span>Vazgeç</span>
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setSelectMode(true)}
                    disabled={bulkBusy || !visibleBugIds.length}
                  >
                    <FiCheckSquare size={15} />
                    <span>İşleri Seç</span>
                  </button>
                )}
              </div>
            </div>
          )}

          <div className={`bug-list${selectMode ? " selecting" : ""}`}>
            {loading
              ? Array.from({ length: 10 }).map((_, i) => <ProjectCardLoader key={i} />)
              : filteredBugCount === 0 ? (
                <div className="bug-filter-empty">
                  <strong>Bu filtrelerle iş bulunamadı</strong>
                  <p>Arama metnini veya filtreleri değiştirerek tekrar deneyebilirsin.</p>
                  {hasActiveFilters ? (
                    <button type="button" onClick={clearBugFilters}>
                      Filtreleri Temizle
                    </button>
                  ) : null}
                </div>
              )
              : filteredVersionList.map((ver) => {
                const verUid = versionUidByName[String(ver.version_name || "")] || "";
                return (
                  <div
                    key={ver.id}
                    className="version-group"
                    id={verUid ? `version-${verUid}` : undefined}
                  >
                    <div className="versionTitle">
                      <h2>{ver.version_name}</h2>

                      {verUid ? (
                        <button
                          type="button"
                          onClick={() => copyText(verUid)}
                          title="Sürüm ID kopyala"
                          className="version-uid"
                        >
                          {verUid}
                        </button>
                      ) : null}

                      <button
                        onClick={() =>
                          setRenameDlg({
                            open: true,
                            id: ver.id,
                            name: ver.version_name
                          })
                        }
                      >
                        <FiEdit2 size={15} />
                      </button>
                      <button onClick={() => openShare(ver.version_name)}>
                        <FiShare size={15} />
                      </button>
                    </div>

                    {[...(filteredBugs[ver.version_name] || [])]
                      .sort((a, b) => Number(isBugSettled(a)) - Number(isBugSettled(b)))
                      .map((b) => {
                        const text = htmlToText(b.description || "");
                        const lines = text.split("\n");
                        const hasMoreThanTwo = lines.length > 2;
                        const preview = hasMoreThanTwo ? lines.slice(0, 2).join("\n") : text;
                        const isOpen = !!expanded[b.id];

                        const extraAtts = attachmentsMap[b.id] || [];
	                        const showChips = extraAtts.length > 0;
	                        const visibleChips = extraAtts.slice(0, 3);
	                        const restCount = Math.max(0, extraAtts.length - visibleChips.length);
	                        const isSaving = !!b.is_saving;
                        const bugKey = getBugKey(b);
                        const isSelected = selectedBugIdSet.has(bugKey);
                        const isCompleted = isBugDone(b);

                        const quickBag = quickData[String(b.id)] || {};
                        const quickTab = quickPanel.bugId === String(b.id) ? quickPanel.tab : "";
                        const currentLabels = quickBag.labels || (Array.isArray(b.labels) ? b.labels : []);
                        const labelCount = currentLabels.length;
                        const checklistTotal = quickBag.checklist
                          ? quickBag.checklist.length
                          : Number(b.checklist_total) || 0;
                        const checklistDone = quickBag.checklist
                          ? quickBag.checklist.filter((item) => toBool(item.is_done)).length
                          : Number(b.checklist_done) || 0;
                        const assigneeCount = quickBag.assignees ? quickBag.assignees.length : null;
                        const commentCount = quickBag.comments ? quickBag.comments.length : null;
                        const attachedLabelIds = new Set(
                          currentLabels.map((label) => String(label?.id ?? "").trim())
                        );
                        const availableLabels = projectLabels.filter(
                          (label) => !attachedLabelIds.has(String(label?.id ?? "").trim())
                        );
                        const assignedUserIds = new Set(
                          (quickBag.assignees || []).map((user) =>
                            String(user?.id ?? user?.user_id ?? "").trim()
                          )
                        );
                        const availableAssignees = projectUsers.filter(
                          (user) => user?.id && !assignedUserIds.has(String(user.id).trim())
                        );

	                        return (
	                          <div key={b.id} className={`bug-card${isCompleted ? " completed" : ""}${isSaving ? " saving" : ""}${isSelected ? " selected" : ""}`}>
	                            <div
	                              className="custom-checkbox"
	                              title={isCompleted ? "Tamamlandı olarak işaretlendi" : "Tamamlandı olarak işaretle"}
	                              onClick={() => {
	                                if (!isSaving) toggleAndRefresh(b.id, isCompleted);
	                              }}
	                            >
	                              <input type="checkbox" checked={isCompleted} readOnly />
	                              <span className="checkmark" />
                            </div>

                            <label className="bulk-card-select" title="Toplu işlem için seç">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleSelectedBug(b)}
                                disabled={isSaving || bulkBusy || !bugKey}
                              />
                              <span />
                            </label>

                            <div className="bug-info">
                              <div className="bug-info-left">
                                <div className="bug-title-row">
                                  <h3 className="bug-title">{b.title}</h3>

                                  {isCompleted && completionGrace[String(b.id)] && (
                                    <button
                                      type="button"
                                      className="bug-undo-chip"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!isSaving) toggleAndRefresh(b.id, true);
                                      }}
                                    >
                                      <FiRotateCcw size={11} aria-hidden="true" />
                                      Geri al
                                    </button>
                                  )}

                                  {b.meta?.priority && (
                                    <span
                                      className="bug-card-priority"
                                      style={{
                                        backgroundColor: `${PRIORITY_COLORS[b.meta.priority] || "#64748b"}1a`,
                                        color: PRIORITY_COLORS[b.meta.priority] || "#64748b",
                                        borderColor: `${PRIORITY_COLORS[b.meta.priority] || "#64748b"}40`
                                      }}
                                    >
                                      <span
                                        className="bug-card-priority-dot"
                                        style={{ backgroundColor: PRIORITY_COLORS[b.meta.priority] || "#64748b" }}
                                      />
                                      {PRIORITY_LABELS[b.meta.priority] || b.meta.priority}
                                    </span>
                                  )}

                                  {b.meta?.due_date && (
                                    <span
                                      className={`bug-card-due${isBugOverdue(b) ? " overdue" : ""}`}
                                      title="Teslim tarihi"
                                    >
                                      <FiClock size={11} aria-hidden="true" />
                                      {formatDueDateShort(b.meta.due_date)}
                                    </span>
                                  )}

                                  {Number(b.checklist_total) > 0 && (
                                    <span
                                      className={`bug-card-checklist${Number(b.checklist_done) >= Number(b.checklist_total) ? " complete" : ""}`}
                                      title="Kontrol listesi ilerlemesi"
                                    >
                                      <FiCheckSquare size={11} aria-hidden="true" />
                                      {Number(b.checklist_done)}/{Number(b.checklist_total)}
                                    </span>
                                  )}

                                  {Array.isArray(b.labels) && b.labels.length > 0 && (
                                    <div className="bug-card-labels">
                                      {b.labels.map((label) => {
                                        const color = label?.color || "#489bf4";
                                        return (
                                          <span
                                            key={label.id}
                                            className="bug-card-label"
                                            style={{
                                              backgroundColor: `${color}1a`,
                                              color,
                                              borderColor: `${color}40`
                                            }}
                                          >
                                            <span
                                              className="bug-card-label-dot"
                                              style={{ backgroundColor: color }}
                                            />
                                            {label.name}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>

                                {b.description && (
                                  <>
                                    {isOpen ? (
                                      <div
                                        className="bug-details expanded"
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(b.description || "") }}
                                      />
                                    ) : (
                                      <p className="bug-details collapsed">{preview}</p>
                                    )}
                                    {hasMoreThanTwo && (
                                      <button
                                        type="button"
                                        className="bug-more"
                                        onClick={() => toggleDetails(b.id)}
                                      >
                                        {isOpen ? "Daha az" : "Daha fazla detay"}
                                      </button>
                                    )}
                                  </>
                                )}

                                {(b.file_url || b.external_url) && (
                                  <div className="bug-attachment">
                                    {b.file_url && (
                                      <>
                                        <a
                                          href={b.file_url}
                                          className="bug-attachment-link"
                                          title="Eki aç"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            openAttachmentInModal(b.file_url, `Ek: ${getFileName(b.file_url)}`);
                                          }}
                                        >
                                          <FiPaperclip />
                                          <span className="file-label">Ek: {getFileName(b.file_url)}</span>
                                        </a>

                                        {isImageUrl(b.file_url) && (
                                          <a
                                            href={b.file_url}
                                            className="bug-attachment-thumb"
                                            title="Önizlemeyi aç"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              openAttachmentInModal(b.file_url, getFileName(b.file_url), "image");
                                            }}
                                          >
                                            <img src={b.file_url} alt="Ek önizleme" />
                                          </a>
                                        )}
                                      </>
                                    )}

                                    {b.external_url && (
                                      <a
                                        href={b.external_url}
                                        className="bug-attachment-link"
                                        title={b.external_url}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          openAttachmentInModal(
                                            b.external_url,
                                            isFirebaseStorageUrl(b.external_url)
                                              ? `Bağlantı: ${getFirebaseObjectPath(b.external_url) || getFileName(b.external_url)}`
                                              : `Bağlantı: ${getHost(b.external_url)}`
                                          );
                                        }}
                                      >
                                        <FiLink />
                                        <span className="file-label">
                                          Bağlantı: {isFirebaseStorageUrl(b.external_url) ? (getFirebaseObjectPath(b.external_url) || getFileName(b.external_url)) : getHost(b.external_url)}
                                        </span>
                                      </a>
                                    )}
                                  </div>
                                )}

                                {showChips && (
                                  <div className="bug-attachment-list">
                                    {visibleChips.map((a) => (
                                      <a
                                        key={a.id}
                                        href={a.file_path}
                                        className="att-chip"
                                        title={a.file_path}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          openAttachmentInModal(a.file_path, getFileName(a.file_path));
                                        }}
                                      >
                                        <FiPaperclip />
                                        <span className="file-label">{getFileName(a.file_path)}</span>
                                      </a>
                                    ))}

                                    {restCount > 0 && (
                                      <button
                                        type="button"
                                        className="att-more"
                                        onClick={() => setDetailId(b.id)}
                                        title="Tüm ekleri gör"
                                      >
                                        +{restCount} daha
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>

	                              <div className="bug-info-right">
	                                <div>
	                                  <button onClick={() => setDetailId(b.id)} disabled={isSaving}>
	                                    <span>Detay</span>
	                                  </button>
	                                  <button onClick={() => startEditBug(b)} disabled={isSaving}>
	                                    <span>Düzenle</span>
	                                  </button>
                                  <button
                                    type="button"
                                    className="ai-prompt-button"
                                    title="Geliştirme promptu kopyala"
                                    disabled={isSaving}
                                    onClick={() =>
                                      copyBugDevelopmentPrompt(b, {
                                        versionName: ver.version_name,
                                        attachments: extraAtts
                                      })
                                    }
                                  >
                                    <FiActivity size={16} />
                                    <span>Geliştir</span>
                                  </button>
                                  <button
                                    type="button"
                                    className="ai-prompt-button"
                                    title="Test senaryosu promptu kopyala"
                                    disabled={isSaving}
                                    onClick={() =>
                                      copyBugTestScenarioPrompt(b, {
                                        versionName: ver.version_name,
                                        attachments: extraAtts
                                      })
                                    }
                                  >
                                    <FiCheck size={16} />
                                    <span>Test</span>
                                  </button>
	                                  <button
	                                    disabled={isSaving}
	                                    onClick={() =>
	                                      setDeleteBugDlg({
                                        open: true,
                                        id: b.id,
                                        title: b.title || "İş"
                                      })
                                    }
                                    className="delete-button"
                                  >
                                    <FiTrash color="#B9B9B9" size={20} />
                                  </button>
                                </div>
                                <div className="bug-info-right-mail">{b.email}</div>
                              </div>
                            </div>

                            <div className="bug-quick">
                              <div className="bug-quick-badges">
                                <button
                                  type="button"
                                  className={`bug-quick-badge${quickTab === "label" ? " active" : ""}`}
                                  onClick={() => openQuickPanel(b.id, "label")}
                                >
                                  <FiTag size={13} aria-hidden="true" />
                                  <span>Etiket</span>
                                  <em>{labelCount}</em>
                                </button>

                                <button
                                  type="button"
                                  className={`bug-quick-badge${quickTab === "checklist" ? " active" : ""}`}
                                  onClick={() => openQuickPanel(b.id, "checklist")}
                                >
                                  <FiCheckSquare size={13} aria-hidden="true" />
                                  <span>Liste</span>
                                  <em>{checklistTotal ? `${checklistDone}/${checklistTotal}` : "0"}</em>
                                </button>

                                <button
                                  type="button"
                                  className={`bug-quick-badge${quickTab === "assignee" ? " active" : ""}`}
                                  onClick={() => openQuickPanel(b.id, "assignee")}
                                >
                                  <FiUsers size={13} aria-hidden="true" />
                                  <span>Kişi</span>
                                  <em>{assigneeCount !== null ? assigneeCount : "–"}</em>
                                </button>

                                <button
                                  type="button"
                                  className={`bug-quick-badge${quickTab === "comment" ? " active" : ""}`}
                                  onClick={() => openQuickPanel(b.id, "comment")}
                                >
                                  <FiMessageSquare size={13} aria-hidden="true" />
                                  <span>Yorum</span>
                                  <em>{commentCount !== null ? commentCount : "–"}</em>
                                </button>
                              </div>

                              {quickTab ? (
                                <div className={`bug-quick-panel${quickBusy ? " busy" : ""}`}>
                                  {quickTab === "label" ? (
                                    <>
                                      <div className="bug-quick-chips">
                                        {(quickBag.labels || b.labels || []).map((label) => (
                                          <span
                                            key={label.id}
                                            className="bug-quick-chip"
                                            style={{ borderColor: `${label.color || "#6e6e73"}55`, color: label.color || "#6e6e73" }}
                                          >
                                            {label.name}
                                            <button
                                              type="button"
                                              onClick={() => quickDetachLabel(b.id, label.id)}
                                              disabled={quickBusy}
                                              aria-label={`${label.name} etiketini kaldır`}
                                            >
                                              <FiX size={11} />
                                            </button>
                                          </span>
                                        ))}
                                        {!(quickBag.labels || b.labels || []).length ? (
                                          <span className="bug-quick-empty">Etiket yok</span>
                                        ) : null}
                                      </div>
                                      <div className="bug-quick-row">
                                        <select
                                          value={quickDraft.label}
                                          onChange={(e) => setQuickDraft((prev) => ({ ...prev, label: e.target.value }))}
                                          disabled={quickBusy || !availableLabels.length}
                                        >
                                          <option value="">
                                            {availableLabels.length ? "Etiket seç..." : "Eklenecek etiket yok"}
                                          </option>
                                          {availableLabels.map((label) => (
                                            <option key={label.id} value={label.id}>{label.name}</option>
                                          ))}
                                        </select>
                                        <button
                                          type="button"
                                          onClick={() => quickAttachLabel(b.id)}
                                          disabled={quickBusy || !quickDraft.label}
                                        >
                                          Ekle
                                        </button>
                                      </div>
                                    </>
                                  ) : null}

                                  {quickTab === "checklist" ? (
                                    <>
                                      <ul className="bug-quick-list">
                                        {(quickBag.checklist || []).map((item) => (
                                          <li key={item.id}>
                                            <label>
                                              <input
                                                type="checkbox"
                                                checked={toBool(item.is_done)}
                                                onChange={() => quickToggleChecklistItem(b.id, item)}
                                                disabled={quickBusy}
                                              />
                                              <span className={toBool(item.is_done) ? "done" : ""}>{item.title}</span>
                                            </label>
                                            <button
                                              type="button"
                                              onClick={() => quickRemoveChecklistItem(b.id, item)}
                                              disabled={quickBusy}
                                              aria-label="Maddeyi sil"
                                            >
                                              <FiX size={12} />
                                            </button>
                                          </li>
                                        ))}
                                        {!(quickBag.checklist || []).length ? (
                                          <li className="bug-quick-empty">Madde yok</li>
                                        ) : null}
                                      </ul>
                                      <div className="bug-quick-row">
                                        <input
                                          type="text"
                                          value={quickDraft.checklist}
                                          placeholder="Yeni madde..."
                                          onChange={(e) => setQuickDraft((prev) => ({ ...prev, checklist: e.target.value }))}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                              e.preventDefault();
                                              quickAddChecklistItem(b.id);
                                            }
                                          }}
                                          disabled={quickBusy}
                                        />
                                        <button
                                          type="button"
                                          onClick={() => quickAddChecklistItem(b.id)}
                                          disabled={quickBusy || !quickDraft.checklist.trim()}
                                        >
                                          Ekle
                                        </button>
                                      </div>
                                    </>
                                  ) : null}

                                  {quickTab === "assignee" ? (
                                    <>
                                      <div className="bug-quick-chips">
                                        {(quickBag.assignees || []).map((user) => (
                                          <span key={user.id || user.user_id} className="bug-quick-chip">
                                            {user.name || user.email || user.user_id}
                                            <button
                                              type="button"
                                              onClick={() => quickUnassignUser(b.id, user.id || user.user_id)}
                                              disabled={quickBusy}
                                              aria-label="Atamayı kaldır"
                                            >
                                              <FiX size={11} />
                                            </button>
                                          </span>
                                        ))}
                                        {!(quickBag.assignees || []).length ? (
                                          <span className="bug-quick-empty">Atanan yok</span>
                                        ) : null}
                                      </div>
                                      <div className="bug-quick-row">
                                        <select
                                          value={quickDraft.assignee}
                                          onChange={(e) => setQuickDraft((prev) => ({ ...prev, assignee: e.target.value }))}
                                          disabled={quickBusy || !availableAssignees.length}
                                        >
                                          <option value="">
                                            {availableAssignees.length ? "Kullanıcı seç..." : "Atanacak kullanıcı yok"}
                                          </option>
                                          {availableAssignees.map((user) => (
                                            <option key={user.id} value={user.id}>{user.name || user.email}</option>
                                          ))}
                                        </select>
                                        <button
                                          type="button"
                                          onClick={() => quickAssignUser(b.id)}
                                          disabled={quickBusy || !quickDraft.assignee}
                                        >
                                          Ata
                                        </button>
                                      </div>
                                    </>
                                  ) : null}

                                  {quickTab === "comment" ? (
                                    <>
                                      <ul className="bug-quick-comments">
                                        {(quickBag.comments || []).slice(-4).map((comment) => (
                                          <li key={comment.id}>
                                            <strong>{comment.user_name || comment.name || comment.email || "Kullanıcı"}</strong>
                                            <p>{comment.content}</p>
                                          </li>
                                        ))}
                                        {!(quickBag.comments || []).length ? (
                                          <li className="bug-quick-empty">Yorum yok</li>
                                        ) : null}
                                      </ul>
                                      <div className="bug-quick-row">
                                        <input
                                          type="text"
                                          value={quickDraft.comment}
                                          placeholder="Kısa bir not bırak..."
                                          onChange={(e) => setQuickDraft((prev) => ({ ...prev, comment: e.target.value }))}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                              e.preventDefault();
                                              quickAddComment(b.id);
                                            }
                                          }}
                                          disabled={quickBusy}
                                        />
                                        <button
                                          type="button"
                                          onClick={() => quickAddComment(b.id)}
                                          disabled={quickBusy || !quickDraft.comment.trim()}
                                        >
                                          Gönder
                                        </button>
                                      </div>
                                    </>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      <BugModal
        open={modal.bug}
        close={() => {
          setIsEdit(false);
          setEditingBugId(null);
          setForm((prev) => ({ ...initialForm, version: prev.version }));
          dispatch({ type: "bug", open: false });
        }}
        form={form}
        setForm={setForm}
        upload={handleFileUpload}
        add={handleAddBug}
        isEdit={isEdit}
        save={handleUpdateBug}
        projectLabels={projectLabels}
        projectId={projectId}
        onLabelCreated={(label) =>
          setProjectLabels((prev) => {
            if (!label || prev.some((item) => String(item.id) === String(label.id))) {
              return prev;
            }
            return [...prev, label].sort((a, b) =>
              String(a.name || "").localeCompare(String(b.name || ""), "tr")
            );
          })
        }
        copyDevelopmentPrompt={copyFormDevelopmentPrompt}
        copyTestScenarioPrompt={copyFormTestScenarioPrompt}
      />

      <VersionModal
        open={modal.version}
        close={() => dispatch({ type: "version", open: false })}
        value={form.version}
        setValue={(v) => setForm((f) => ({ ...f, version: v }))}
        create={createVersion}
      />

      <UserModal
        open={modal.user}
        close={() => dispatch({ type: "user", open: false })}
        email={form.email}
        setEmail={(e) => setForm((f) => ({ ...f, email: e }))}
        add={handleInviteUser}
        users={projectUsers}
        removeUser={handleRemoveProjectUser}
        changeUserRole={handleChangeProjectUserRole}
        canManageMembers={canManageProjectUsers}
        currentUserId={currentUserId}
        currentUserEmail={currentUserEmail}
      />

	      <InfoModal
	        open={modal.info}
	        close={() => dispatch({ type: "info", open: false })}
	        projectName={displayProjectName}
	        projectId={projectId}
	        isOwner={isOwner === "1"}
	        shareUrl={() => shareProjectInvite(displayProjectName, projectId)}
	        leave={leave}
	        askDelete={() => dispatch({ type: "deleteConfirm", open: true })}
	      />

	      <DeleteModal
	        open={modal.deleteConfirm}
	        close={() => dispatch({ type: "deleteConfirm", open: false })}
	        projectName={displayProjectName}
	        confirm={form.confirmName}
	        setConfirm={(c) => setForm((f) => ({ ...f, confirmName: c }))}
	        remove={async () => {
          await removeProject();
          navigate("/");
        }}
      />

	      <ShareModal
	        open={modal.share}
	        close={() => dispatch({ type: "share", open: false })}
	        preview={form.preview}
	        projectName={displayProjectName}
	        version={form.nowVersion}
	      />

      <RenameVersionModal
        open={renameDlg.open}
        current={renameDlg.name}
        close={() => setRenameDlg({ ...renameDlg, open: false })}
        save={(n) => renameVersion(renameDlg.id, n)}
      />

      <DeadlineModal
        open={deadlineDlg.open}
        currentDate={deadlineDlg.date}
        close={() => setDeadlineDlg({ ...deadlineDlg, open: false })}
        save={saveDeadline}
      />

      <Modal open={completeOpen} close={() => setCompleteOpen(false)}>
        <h2>Sürümü Tamamla</h2>
        <p className="modal-desc">
          “{lastVersion?.version_name}” sürümünü tamamlamak istediğinize emin misiniz?
        </p>
        <div className="modal-buttons">
          <button
            type="button"
            className="modal-secondary-button"
            onClick={() => setCompleteOpen(false)}
          >
            İptal
          </button>
          <button
            type="button"
            className="modal-add-button"
            onClick={async () => {
              await closeVersion();
              setCompleteOpen(false);
              showToast({
                title: "Sürüm tamamlandı",
                description: `“${lastVersion?.version_name}” kapatıldı.`,
                variant: "success",
                position: "bottom-right"
              });
              dispatch({ type: "version", open: true });
            }}
          >
            Evet, Tamamla
          </button>
        </div>
      </Modal>

      <Modal open={reopenOpen} close={() => setReopenOpen(false)}>
        <h2>Sürümü Yeniden Aç</h2>
        <p className="modal-desc">
          “{lastVersion?.version_name}” sürümünü yeniden açmak istediğinize emin misiniz?
        </p>
        <div className="modal-buttons">
          <button
            type="button"
            className="modal-secondary-button"
            onClick={() => setReopenOpen(false)}
          >
            İptal
          </button>
          <button
            type="button"
            className="modal-add-button"
            onClick={async () => {
              await reopenVersion();
              setReopenOpen(false);
              showToast({
                title: "Sürüm açıldı",
                description: `“${lastVersion?.version_name}” tekrar açıldı.`,
                variant: "success",
                position: "bottom-right"
              });
            }}
          >
            Evet, Aç
          </button>
        </div>
      </Modal>

      <Modal
        open={deleteBugDlg.open}
        close={() => setDeleteBugDlg({ open: false, id: null, title: "" })}
      >
        <div className="bug-delete-confirm">
          <h2>İşi Sil</h2>
          <p className="modal-desc">
            “{deleteBugDlg.title}” işini silmek istediğinize emin misiniz?
          </p>
          <div className="modal-buttons">
            <button
              type="button"
              className="modal-secondary-button"
              onClick={() => setDeleteBugDlg({ open: false, id: null, title: "" })}
            >
              İptal
            </button>
            <button
              type="button"
              className="modal-add-button"
              onClick={async () => {
                if (deleteBugDlg.id) {
                  await removeAndRefresh(deleteBugDlg.id, deleteBugDlg.title);
                }
                setDeleteBugDlg({ open: false, id: null, title: "" });
              }}
            >
              Tamam
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={bulkDeleteDlg}
        close={() => {
          if (!bulkBusy) setBulkDeleteDlg(false);
        }}
      >
        <div className="bug-delete-confirm">
          <h2>Seçili İşleri Sil</h2>
          <p className="modal-desc">
            {selectedBugEntries.length} seçili işi silmek istediğinize emin misiniz?
          </p>
          <div className="modal-buttons">
            <button
              type="button"
              className="modal-secondary-button"
              onClick={() => setBulkDeleteDlg(false)}
              disabled={bulkBusy}
            >
              İptal
            </button>
            <button
              type="button"
              className="modal-add-button"
              onClick={deleteSelectedBugs}
              disabled={bulkBusy}
            >
              {bulkBusy ? "Siliniyor..." : "Tamam"}
            </button>
          </div>
        </div>
      </Modal>

	      <EditProjectModal
	        open={editModalOpen}
	        close={() => setEditModalOpen(false)}
	        projectId={projectId}
	        projectName={displayProjectName}
	        onSaveTitle={handleSaveProjectTitle}
	        onResetTitle={handleResetProjectTitle}
	        defaultProjectName={originalProjectNameRef.current}
	      />

	      {detailId && (
	        <BugDetail
	          bugId={detailId}
	          projectName={displayProjectName}
	          onClose={() => setDetailId(null)}
	          onEditBug={(detailBug) => {
	            setDetailId(null);
	            startEditBug(detailBug);
	          }}
	          onStatusChange={refreshShadow}
	        />
	      )}

      <FilePreviewModal
        open={attViewer.open}
        close={() => setAttViewer({ open: false, url: "", title: "", kind: "" })}
        url={attViewer.url}
        title={attViewer.title}
        kind={attViewer.kind}
        onCopy={(value) => copyText(value)}
        onDownload={(value, name) => downloadFile(value, name)}
      />
    </div>
  );
};

export default Bugs;
